/**
 * Thin fetch wrapper for talking to agent-portal-api.
 *
 * - Always sends cookies (credentials: 'include').
 * - Throws ApiError on non-2xx responses with parsed { type, message, details }.
 * - Returns the unwrapped `data` field for { data, ... } responses.
 */

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:4003";

export class ApiError extends Error {
  public readonly status: number;
  public readonly type: string | undefined;
  public readonly details: unknown;

  constructor(
    status: number,
    message: string,
    type?: string,
    details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.type = type;
    this.details = details;
  }
}

interface ApiOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Path under the API root, e.g. "/api/auth/me" */
  path: string;
}

// Shared fetch + parse + error handling. Returns the raw JSON payload (or
// undefined for 204) so callers can either unwrap `data` or keep the envelope.
async function rawFetch(opts: ApiOptions): Promise<unknown> {
  const { path, body, headers, ...rest } = opts;

  const init: RequestInit = {
    credentials: "include",
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...rest,
  };

  const res = await fetch(`${API_URL}${path}`, init);

  // 204 / empty body
  if (res.status === 204) {
    return undefined;
  }

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const err = (
      payload as {
        error?: { type?: string; message?: string; details?: unknown };
      } | null
    )?.error;
    throw new ApiError(
      res.status,
      err?.message ?? `Request failed (${res.status})`,
      err?.type,
      err?.details,
    );
  }

  return payload;
}

async function request<T>(opts: ApiOptions): Promise<T> {
  const payload = await rawFetch(opts);

  // Convention: API returns { data, meta? }. Unwrap.
  if (payload && typeof payload === "object" && "data" in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

// Like `request` but keeps the full `{ data, meta }` envelope — for list
// endpoints whose UI needs `meta.total`.
async function requestEnvelope<T>(opts: ApiOptions): Promise<T> {
  return (await rawFetch(opts)) as T;
}

export const api = {
  get: <T>(path: string, init?: Omit<ApiOptions, "path" | "body" | "method">) =>
    request<T>({ ...init, path, method: "GET" }),
  post: <T>(
    path: string,
    body?: unknown,
    init?: Omit<ApiOptions, "path" | "body" | "method">,
  ) => request<T>({ ...init, path, method: "POST", body }),
  patch: <T>(
    path: string,
    body?: unknown,
    init?: Omit<ApiOptions, "path" | "body" | "method">,
  ) => request<T>({ ...init, path, method: "PATCH", body }),
  put: <T>(
    path: string,
    body?: unknown,
    init?: Omit<ApiOptions, "path" | "body" | "method">,
  ) => request<T>({ ...init, path, method: "PUT", body }),
  delete: <T>(
    path: string,
    init?: Omit<ApiOptions, "path" | "body" | "method">,
  ) => request<T>({ ...init, path, method: "DELETE" }),
};

// ─── Typed helpers per endpoint ──────────────────────────────────────────────

export type UserType = "INTERNAL" | "AGENT" | "SUB_AGENT" | "GODADDY_MERCHANT";
export type UserStatus = "INVITED" | "ACTIVE" | "DISABLED";
export type AccessRequestStatus = "PENDING" | "APPROVED" | "DENIED";

export interface CurrentUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  userType: UserType;
  status: UserStatus;
  createdAt: string;
  roles: string[];
  /** True when an admin has assigned a temporary password and the user has
   *  not yet picked their own. Drives the /force-password-reset interstitial. */
  mustResetPassword: boolean;
  /** Deadline (ISO) until which the user can still skip the forced reset.
   *  Past this point the Skip button is hidden and reset becomes mandatory. */
  tempPasswordGraceUntil: string | null;
  /** Whether email-based 2FA is enabled for this account. */
  twoFactorEnabled: boolean;
  impersonating: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    userType: UserType;
    createdAt: string;
  } | null;
}

/** Returned by sign-in when the account has 2FA enabled — no session yet. */
export interface TwoFactorRequired {
  requires2fa: true;
  challengeId: string;
  email: string;
}

/** Type guard: did sign-in return a 2FA challenge instead of a user? */
export function isTwoFactorRequired(
  result: CurrentUser | TwoFactorRequired,
): result is TwoFactorRequired {
  return (result as TwoFactorRequired).requires2fa === true;
}

export interface UpdateMeInput {
  firstName?: string;
  lastName?: string;
  phone?: string | null;
}

export interface UserListItem {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  userType: UserType;
  status: UserStatus;
  parentAgentId?: string | null;
  roles: string[];
  // Not served yet; commissions show them once they are.
  nickname?: string | null;
  companyName?: string | null;
}

export interface AccessRequest {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  reason: string | null;
  requestedUserType: UserType | null;
  status: AccessRequestStatus;
  reviewedAt: string | null;
  reviewedBy: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  denialReason: string | null;
  /** The account approval created; null until approved. */
  createdUserId: string | null;
  createdAt: string;
}

export const authApi = {
  me: () => api.get<CurrentUser>("/api/auth/me"),
  updateMe: (body: UpdateMeInput) =>
    api.patch<CurrentUser>("/api/auth/me", body),
  // Returns the user on success, or a 2FA challenge when the account has email
  // 2FA enabled (use isTwoFactorRequired to discriminate).
  signIn: (email: string, password: string) =>
    api.post<CurrentUser | TwoFactorRequired>("/api/auth/sign-in", {
      email,
      password,
    }),
  // Complete a 2FA-gated sign-in. On success the session cookie is set and the
  // user is returned.
  verifyTwoFactor: (challengeId: string, code: string) =>
    api.post<CurrentUser>("/api/auth/verify-2fa", { challengeId, code }),
  // Re-send the login code for an in-flight challenge (returns the new id).
  resendTwoFactor: (challengeId: string) =>
    api.post<{ challengeId: string }>("/api/auth/resend-2fa", { challengeId }),
  // Account → Security: emails a confirmation code; `purpose` reflects whether
  // confirming will turn 2FA on or off (derived from current state server-side).
  setupTwoFactor: () =>
    api.post<{ challengeId: string; purpose: "ENABLE" | "DISABLE" }>(
      "/api/auth/2fa/setup",
    ),
  // Verify the setup code and flip the flag; returns the new state.
  confirmTwoFactor: (challengeId: string, code: string) =>
    api.post<{ twoFactorEnabled: boolean }>("/api/auth/2fa/confirm", {
      challengeId,
      code,
    }),
  setPassword: (token: string, password: string) =>
    api.post<{ ok: boolean }>("/api/auth/set-password", { token, password }),
  requestPasswordReset: (email: string) =>
    api.post<{ ok: boolean }>("/api/auth/request-password-reset", { email }),
  // `currentPassword` is omitted in the forced-reset flow (user is already
  // signed in with the temp password). Required for normal account-settings
  // rotations — the backend rejects if missing in that mode.
  changePassword: (newPassword: string, currentPassword?: string) =>
    api.post<{ ok: boolean }>("/api/auth/change-password", {
      newPassword,
      ...(currentPassword !== undefined ? { currentPassword } : {}),
    }),
  logout: () =>
    api.post<{ logoutUrl?: string; message?: string }>("/api/auth/logout"),
  // Legacy WorkOS hosted UI URL — kept for SSO flow later.
  loginUrl: () => `${API_URL}/api/auth/login`,
};

export interface UsersListParams {
  search?: string;
  /** User types (INTERNAL/AGENT/SUB_AGENT/GODADDY_MERCHANT). */
  type?: string[];
  /** User statuses (ACTIVE/INVITED/DISABLED). */
  status?: string[];
  includeDisabled?: boolean;
  page?: number;
  limit?: number;
}

export interface UsersListResponse {
  data: UserListItem[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

function buildUsersQuery(params: UsersListParams): string {
  const sp = new URLSearchParams();
  if (params.search) sp.set("search", params.search);
  if (params.includeDisabled) sp.set("includeDisabled", "true");
  if (params.page) sp.set("page", String(params.page));
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.type?.length) sp.set("type", params.type.join(","));
  if (params.status?.length) sp.set("status", params.status.join(","));
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const userApi = {
  list: (params: { includeDisabled?: boolean } = {}) => {
    const qs = new URLSearchParams();
    if (params.includeDisabled) qs.set("includeDisabled", "true");
    const q = qs.toString();
    return api.get<UserListItem[]>(`/api/users${q ? `?${q}` : ""}`);
  },
  // Server-paginated + server-filtered variant; keeps the { data, meta } envelope
  // so the admin table can show the total and page through results.
  listPaged: (params: UsersListParams = {}) =>
    requestEnvelope<UsersListResponse>({
      path: `/api/users${buildUsersQuery(params)}`,
      method: "GET",
    }),
  listAgents: () => api.get<UserListItem[]>("/api/users/agents"),
  create: (body: {
    email: string;
    firstName?: string;
    lastName?: string;
    /** Optional — derived server-side from the account role in `roles`.
     *  AGENT callers may omit it (the backend forces SUB_AGENT). */
    userType?: UserType;
    /** Account role + any permission roles. AGENT callers may omit (backend
     *  forces a sub_agent). Internal creates pass exactly one account role. */
    roles?: string[];
    /** Required when an INTERNAL admin creates a SUB_AGENT. */
    parentAgentId?: string | null;
  }) => api.post<UserListItem>("/api/users", body),
  update: (
    userId: string,
    body: {
      firstName?: string;
      lastName?: string;
      userType?: UserType;
      status?: "ACTIVE" | "DISABLED";
      parentAgentId?: string | null;
    },
  ) => api.patch<UserListItem>(`/api/users/${userId}`, body),
  updateRoles: (userId: string, roles: string[]) =>
    api.patch<UserListItem>(`/api/users/${userId}/roles`, { roles }),
  resendInvite: (userId: string) =>
    api.post<{ sent: boolean }>(`/api/users/${userId}/resend-invite`),
  sendLink: (userId: string) =>
    api.post<{ sent: boolean }>(`/api/users/${userId}/send-link`),
  setTempPassword: (userId: string, password: string) =>
    api.post<{ ok: boolean; graceUntil: string }>(
      `/api/users/${userId}/set-temp-password`,
      { password },
    ),
  remove: (userId: string) =>
    api.delete<{ id: string; deleted: boolean }>(`/api/users/${userId}`),
  startImpersonation: (userId: string) =>
    api.post<{
      sessionId: string;
      impersonatedUserId: string;
      expiresAt: string;
    }>(`/api/users/${userId}/impersonate`),
  endImpersonation: () =>
    api.post<{ message: string }>("/api/impersonation/end"),
};

// ─── Applications ────────────────────────────────────────────────────────────

export type ApplicationType =
  | "STANDARD"
  | "PAPER"
  | "LEAD"
  | "SEND_LINK"
  | "PRE_VET"
  | "ISSUING";
export type ApplicationSource =
  | "INTERNAL_CREATE"
  | "AGENT_CREATE"
  | "GHL_WEBHOOK"
  | "SELF_SERVE_LINK";
export type ProcessorSubmissionStatus =
  | "PENDING"
  | "VALIDATING"
  | "SUBMITTING"
  | "SUBMITTED"
  | "FORM_FILLED"
  | "DOCS_UPLOADED"
  | "AWAITING_SIGNATURE"
  | "SIGNED"
  | "APPROVED"
  | "PENDED"
  | "DECLINED"
  | "ERROR";

export interface UserRef {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

export interface Application {
  id: string;
  applicationType: ApplicationType;
  source: ApplicationSource;
  stage: string;
  businessName: string;
  dbaName: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  mccCode: string | null;
  businessAddressState: string | null;
  preferredProcessor: string | null;
  monthlyVolumeCents: number | null;
  declineReason: string | null;
  formData: Record<string, unknown>;
  createdById: string;
  /** Joined creator user info (resolved server-side). */
  createdBy: UserRef | null;
  assignedAgentId: string | null;
  /** Joined assigned-agent user info, if any. */
  assignedAgent: UserRef | null;
  subAgentId: string | null;
  /** Joined sub-agent user info, if any. */
  subAgent: UserRef | null;
  assignedAt: string | null;
  // Processor-related fields are stripped for non-INTERNAL users by the API.
  currentProcessorCode?: string | null;
  currentProcessorStatus?: ProcessorSubmissionStatus | null;
  /**
   * Joined from `processor_configs` via `current_processor_config_id`. Use this
   * for display — `currentProcessorCode` remains as the natural key for
   * adapter lookups but `currentProcessor.name` is the canonical display name.
   */
  currentProcessor?: {
    id: number;
    code: string;
    name: string;
  } | null;
  submittedToUnderwritingAt?: string | null;
  approvedAt: string | null;
  deployedAt: string | null;
  closedAt: string | null;
  ghlContactId: string | null;
  ghlOpportunityId: string | null;
  ghlPipelineId: string | null;
  ghlStageId: string | null;
  ghlLastSyncAt: string | null;
  lockVersion: number;
  /** Save-for-later flag: an in-progress draft, private to its creator's org. */
  isDraft: boolean;
  isDeleted: boolean;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProcessorSubmission {
  id: string;
  applicationId: string;
  processorCode: string;
  status: ProcessorSubmissionStatus;
  submittedById: string | null;
  submittedAt: string | null;
  requestPayload: Record<string, unknown> | null;
  responseData: Record<string, unknown> | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateApplicationInput {
  applicationType?: ApplicationType;
  /** Optional starting stage. Server defaults to new_lead and role-gates this. */
  stage?: string;
  businessName: string;
  dbaName?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  mccCode?: string | null;
  businessAddressState?: string | null;
  preferredProcessor?: string | null;
  monthlyVolumeCents?: number | null;
  formData?: Record<string, unknown>;
  assignedAgentId?: string | null;
  subAgentId?: string | null;
  /** Create as a save-for-later draft (no create side-effects). */
  isDraft?: boolean;
}

export interface UpdateApplicationInput {
  businessName?: string;
  dbaName?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  mccCode?: string | null;
  businessAddressState?: string | null;
  preferredProcessor?: string | null;
  monthlyVolumeCents?: number | null;
  declineReason?: string | null;
  formData?: Record<string, unknown>;
  lockVersion?: number;
  /** Keep saving a draft (true) or finalize it into a real application (false). */
  isDraft?: boolean;
}

export interface ListApplicationsParams {
  stage?: string[];
  type?: string[];
  processor?: string[];
  assignedAgentId?: string[];
  /** Show only the current user's save-for-later drafts (excluded by default). */
  draft?: boolean;
  /** Include soft-deleted applications alongside the live ones. INTERNAL-only. */
  deleted?: boolean;
  updatedWithinDays?: number;
  search?: string;
  sort?: string;
  dir?: "asc" | "desc";
  page?: number;
  limit?: number;
}

export interface ApplicationsListResponse {
  data: Application[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

// Multi-value filters serialize as REPEATED params (`?stage=a&stage=b`) so the
// API's array parser sees each value — matching the merchants query builder.
function buildApplicationsQuery(params: ListApplicationsParams): string {
  const sp = new URLSearchParams();
  if (params.search) sp.set("search", params.search);
  if (params.sort) sp.set("sort", params.sort);
  if (params.dir) sp.set("dir", params.dir);
  if (params.draft) sp.set("draft", "true");
  if (params.deleted) sp.set("deleted", "true");
  if (params.updatedWithinDays)
    sp.set("updatedWithinDays", String(params.updatedWithinDays));
  if (params.page) sp.set("page", String(params.page));
  if (params.limit) sp.set("limit", String(params.limit));
  for (const s of params.stage ?? []) sp.append("stage", s);
  for (const t of params.type ?? []) sp.append("type", t);
  for (const p of params.processor ?? []) sp.append("processor", p);
  for (const a of params.assignedAgentId ?? []) sp.append("assignedAgentId", a);
  const s = sp.toString();
  return s ? `?${s}` : "";
}

function qs(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== "",
  );
  if (entries.length === 0) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of entries) sp.set(k, String(v));
  return `?${sp.toString()}`;
}

export type DocumentReviewStatus = "PENDING" | "APPROVED" | "REJECTED";

// ─── Elavon boarding documents ───────────────────────────────────────────────

export type ElavonDocumentStatus =
  | "not_generated"
  | "generated"
  | "stale"
  | "awaiting_signature"
  | "declined"
  | "signed"
  | "uploaded";

export interface ElavonBoardingDocumentRow {
  code: string;
  signable: boolean;
  status: ElavonDocumentStatus;
  unsignedDocumentId: string | null;
  signedDocumentId: string | null;
  docusealTemplateId: number | null;
  generatedAt: string | null;
  sentAt: string | null;
  signedAt: string | null;
  declinedAt: string | null;
  uploadedAt: string | null;
  elavonDocumentId: string | null;
}

export interface ElavonDocumentListing {
  code: string;
  contentType: string | null;
  signType: string | null;
  contextType: string | null;
  agreementSnippet: boolean;
}

export interface ElavonBoardingOverview {
  boarding: {
    boardingId: string | null;
    boardingStatus: string | null;
    isBoarded: boolean;
    /** Underwriting errors / pend reasons from the last board-status poll. */
    responseData: Record<string, unknown> | null;
  };
  /** Null when the live /listdocuments call failed — the panel still renders. */
  listing: ElavonDocumentListing[] | null;
  documents: ElavonBoardingDocumentRow[];
}

export interface ApplicationDocument {
  id: string;
  applicationId: string;
  docType: string;
  fileName: string;
  storageKey: string;
  contentType: string | null;
  sizeBytes: number | null;
  uploadedById: string | null;
  uploadedAt: string;
  reviewStatus: DocumentReviewStatus;
  reviewedById: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
}

export type DocusealTemplateCategory = "submitted" | "default";

/** A DocuSeal template option for the "send for signature" picker. */
export interface DocusealTemplate {
  id: number;
  name: string;
  slug: string | null;
  folderName: string | null;
  /** "submitted" when the current user has already sent this template. */
  category: DocusealTemplateCategory;
  updatedAt: string | null;
}

/** A template's named signer parties (roles), in signing order. */
export interface DocusealTemplateDetail {
  id: number;
  name: string;
  roles: string[];
}

/** One signer on a submission — request shape + status echoed back. */
export interface DocusealSigner {
  email: string;
  name: string | null;
  role: string | null;
  signingUrl?: string | null;
  status?: "pending" | "signed" | "declined";
}

/** Result of POST /api/applications/:id/docuseal (send for signature). */
export interface DocusealStartResult {
  configured: boolean;
  submissionId: number | null;
  signingUrl: string | null;
  recipientEmail: string;
  signers: DocusealSigner[];
  status: "AWAITING_SIGNATURE";
}

/** Latest DocuSeal submission for an application — backs the E-Sign tab. */
export type DocusealStatus =
  | { exists: false }
  | {
      exists: true;
      id: string;
      status: ProcessorSubmissionStatus | null;
      docusealSubmissionId: number | null;
      templateId: number | null;
      templateName: string | null;
      recipientEmail: string | null;
      recipientName: string | null;
      signers: DocusealSigner[];
      signingUrl: string | null;
      sentByName: string | null;
      sentAt: string | null;
      signedAt: string | null;
      declinedAt: string | null;
      signedDocumentId: string | null;
    };

/** An application stuck in a processor-facing stage past the threshold. */
export interface StuckApplication {
  id: string;
  businessName: string;
  stage: string;
  currentProcessorCode: string | null;
  currentProcessorStatus: string | null;
  assignedAgentId: string | null;
  updatedAt: string;
  stuckDays: number;
}

/** Result of POST /api/applications/:id/reveal — decrypted values for one section. */
export interface RevealSensitiveResult {
  section: "owner" | "banking" | "business";
  ownerIndex?: number;
  ssn?: string | null;
  driverLicense?: string | null;
  routingNumber?: string | null;
  accountNumber?: string | null;
  federalTaxId?: string | null;
}

export interface PublicApplicationDocument {
  id: string;
  docType: string;
  fileName: string;
  contentType: string | null;
  sizeBytes: number | null;
  uploadedAt: string;
  reviewStatus: DocumentReviewStatus;
  reviewNote: string | null;
}

export interface PublicApplicationSummary {
  id: string;
  businessName: string;
  dbaName: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactEmail: string | null;
  stage: string;
}

export type UploadLinkStatus = "PENDING" | "SUBMITTED" | "REVOKED";
export type UploadLinkPurpose = "DOCS" | "FORM";
export type PublicLinkState = "active" | "submitted" | "expired" | "revoked";

export interface PublicUploadLink {
  id: string;
  status: UploadLinkStatus;
  purpose: UploadLinkPurpose;
  expiresAt: string;
  submittedAt: string | null;
  revokedAt: string | null;
}

export interface PublicApplicationPayload {
  state: PublicLinkState;
  application: PublicApplicationSummary;
  link: PublicUploadLink;
  documents: PublicApplicationDocument[];
  requiredDocTypes: string[];
}

export interface ApplicationUploadLinkSummary {
  id: string;
  status: UploadLinkStatus;
  sentToEmail: string | null;
  customMessage: string | null;
  createdAt: string;
  expiresAt: string;
  submittedAt: string | null;
  revokedAt: string | null;
  token: string;
}

export interface SendUploadLinkResult {
  sent: boolean;
  uploadUrl: string;
  linkId: string;
  token: string;
  expiresAt: string;
}

// SEND_LINK flow — mint + email a self-serve application form link. No
// application row exists until the merchant submits the form.
export interface SendApplicationLinkInput {
  merchantEmail: string;
  merchantName?: string | null;
  businessName?: string | null;
  expiresInHours?: number;
}

export interface SendApplicationLinkResult {
  sent: boolean;
  linkId: string;
  applicationUrl: string;
  expiresAt: string;
}

/** Payload of GET /api/public/application-links/:linkId/:token. */
export interface PublicFormLinkPayload {
  state: PublicLinkState;
  prefill: {
    businessName: string | null;
    merchantName: string | null;
    email: string | null;
  };
  link: {
    id: string;
    status: UploadLinkStatus;
    purpose: UploadLinkPurpose;
    expiresAt: string;
    submittedAt: string | null;
    revokedAt: string | null;
  };
}

const API_BASE = API_URL;

export const applicationsApi = {
  list: (params: ListApplicationsParams = {}) =>
    api.get<Application[]>(
      `/api/applications${buildApplicationsQuery(params)}`,
    ),
  // Keeps the `{ data, meta }` envelope so the list UI can page lazily and show
  // `meta.total`.
  listPage: (params: ListApplicationsParams = {}) =>
    requestEnvelope<ApplicationsListResponse>({
      path: `/api/applications${buildApplicationsQuery(params)}`,
      method: "GET",
    }),
  get: (id: string) => api.get<Application>(`/api/applications/${id}`),
  create: (body: CreateApplicationInput) =>
    api.post<Application>("/api/applications", body),
  update: (id: string, body: UpdateApplicationInput) =>
    api.patch<Application>(`/api/applications/${id}`, body),
  assign: (
    id: string,
    body: { assignedAgentId: string | null; subAgentId?: string | null },
  ) => api.patch<Application>(`/api/applications/${id}/assign`, body),
  changeStage: (
    id: string,
    body: {
      stage: string;
      reason?: string | null;
      notes?: string | null;
      mid?: string | null;
      /** Corpay, required by the API when approving an ISSUING application. */
      corpayAccountCode?: string | null;
      corpayCustomerId?: string | null;
      corpayCardTypes?: ("VIRTUAL" | "PHYSICAL" | "GHOST")[] | null;
      /** Manual send: records the destination processor on the application. */
      processorCode?: string | null;
      /** Skip the assigned-agent notification (in-app + email) for this move. */
      isSilentNotifications?: boolean;
    },
  ) => api.patch<Application>(`/api/applications/${id}/stage`, body),
  submitToProcessor: (
    id: string,
    body: { processorCode: string; requestPayload?: Record<string, unknown> },
  ) =>
    api.post<ProcessorSubmission>(`/api/applications/${id}/submissions`, body),
  listSubmissions: (id: string) =>
    api.get<ProcessorSubmission[]>(`/api/applications/${id}/submissions`),
  refreshStatus: (id: string) =>
    api.post<{
      refreshed: boolean;
      outcome?: string;
      previousStatus?: ProcessorSubmissionStatus;
      status?: ProcessorSubmissionStatus;
      reason?: string;
      /** The processor's own feedback — present even when nothing changed. */
      responseData?: Record<string, unknown> | null;
      checkedAt?: string;
    }>(`/api/applications/${id}/refresh-status`, {}),
  remove: (id: string) =>
    api.delete<{ id: string; deleted: boolean }>(`/api/applications/${id}`),
  /** Undo a soft delete. Distinct from the archived → new_lead stage restore. */
  restoreDeleted: (id: string) =>
    api.post<{ id: string; deleted: boolean }>(
      `/api/applications/${id}/restore`,
      {},
    ),
  /** Un-archive: return an archived app to its pre-archive stage. */
  unarchive: (id: string) =>
    api.post<Application>(`/api/applications/${id}/unarchive`, {}),
  /** Discard a save-for-later draft — owner-scoped (agents may delete their own). */
  discardDraft: (id: string) =>
    api.delete<{ id: string; deleted: boolean }>(
      `/api/applications/${id}/draft`,
    ),

  // Decrypt saved sensitive fields for one section (INTERNAL-only, audited server-side).
  revealSensitive: (
    id: string,
    body: { section: "owner" | "banking" | "business"; ownerIndex?: number },
  ) => api.post<RevealSensitiveResult>(`/api/applications/${id}/reveal`, body),

  // Lifecycle
  /** Live DocuSeal templates for the "send for signature" picker (INTERNAL). */
  listDocusealTemplates: (q?: string) =>
    api.get<DocusealTemplate[]>(
      `/api/docuseal/templates${q ? `?q=${encodeURIComponent(q)}` : ""}`,
    ),
  /**
   * Mint a JWT for the embedded DocuSeal form builder. Omit `templateId` to
   * create a new template, or pass it to edit an existing one. INTERNAL-only.
   */
  getDocusealBuilderToken: (
    body: {
      templateId?: number;
      name?: string;
      externalId?: string;
      folderName?: string;
      documentUrls?: string[];
    } = {},
  ) => api.post<{ token: string }>("/api/docuseal/builder-token", body),
  /** Clone a DocuSeal template (fields/mappings copied). INTERNAL-only. */
  cloneDocusealTemplate: (
    id: number,
    body: { name?: string; folderName?: string } = {},
  ) => api.post<DocusealTemplate>(`/api/docuseal/templates/${id}/clone`, body),
  /** Archive a DocuSeal template (soft-delete). INTERNAL-only. */
  archiveDocusealTemplate: (id: number) =>
    api.delete<{ id: number; archivedAt: string | null }>(
      `/api/docuseal/templates/${id}`,
    ),
  /**
   * Create a DocuSeal submission (emails the signer) and move the application
   * to the "signature" stage. INTERNAL-only.
   */
  startDocuseal: (
    id: string,
    body: {
      templateId: number;
      signers: Array<{
        email: string;
        name?: string | null;
        role?: string | null;
      }>;
      notes?: string | null;
    },
  ) => api.post<DocusealStartResult>(`/api/applications/${id}/docuseal`, body),
  /** A single template's signer parties (roles) for the recipient mapping UI. */
  getDocusealTemplate: (templateId: number) =>
    api.get<DocusealTemplateDetail>(`/api/docuseal/templates/${templateId}`),
  /** Latest DocuSeal submission status for the E-Sign tab. */
  getDocusealStatus: (id: string) =>
    api.get<DocusealStatus>(`/api/applications/${id}/docuseal`),
  // ─── Elavon boarding documents (INTERNAL) ──────────────────────────────────
  /** Everything the Elavon boarding panel renders, in one call. */
  getElavonBoarding: (id: string) =>
    api.get<ElavonBoardingOverview>(`/api/applications/${id}/elavon/documents`),
  /** Render the documents at Elavon and store them against the application. */
  fetchElavonDocuments: (
    id: string,
    body: { codes?: string[]; html?: boolean } = {},
  ) =>
    api.post<{
      stored: Array<{
        id: string;
        code: string;
        fileName: string;
        sizeBytes: number;
        signable: boolean;
      }>;
      /** Codes Elavon lists but wouldn't render for this merchant. */
      skipped: Array<{ code: string; reason: string }>;
    }>(`/api/applications/${id}/elavon/documents/fetch`, body),
  /** Turn a generated PDF into an empty DocuSeal template for field placement. */
  createElavonTemplate: (id: string, code: string) =>
    api.post<{ templateId: number; created: boolean }>(
      `/api/applications/${id}/elavon/documents/${code}/template`,
      {},
    ),
  /** Email the merchant a signature request for one Elavon agreement. */
  sendElavonForSignature: (
    id: string,
    code: string,
    body: {
      signers: Array<{
        email: string;
        name?: string | null;
        role?: string | null;
      }>;
      notes?: string | null;
    },
  ) =>
    api.post<{ submissionId: number | null; signers: DocusealSigner[] }>(
      `/api/applications/${id}/elavon/documents/${code}/send`,
      body,
    ),
  /** Push stored documents to Elavon's underwriters via /uploadDocuments. */
  uploadElavonDocuments: (id: string, body: { documentIds: string[] }) =>
    api.post<
      Array<{ documentId: string | null; documentStatus: string | null }>
    >(`/api/applications/${id}/elavon/documents/upload`, body),

  pollProcessorStatus: () =>
    api.post<{ checked: number; updated: number; skipped: number }>(
      "/api/applications/poll-status",
      {},
    ),
  listStuck: (days?: number) =>
    api.get<StuckApplication[]>(
      `/api/applications/stuck${days ? `?days=${days}` : ""}`,
    ),

  // ─── Documents ───────────────────────────────────────────────────────────
  listDocuments: (id: string) =>
    api.get<ApplicationDocument[]>(`/api/applications/${id}/documents`),
  uploadDocument: async (id: string, docType: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("docType", docType);
    const res = await fetch(
      `${API_BASE}/api/applications/${id}/documents/upload`,
      {
        method: "POST",
        credentials: "include",
        body: fd,
      },
    );
    if (!res.ok) {
      const payload = (await res.json().catch(() => null)) as {
        error?: { message?: string };
      } | null;
      throw new ApiError(
        res.status,
        payload?.error?.message ?? `Upload failed (${res.status})`,
      );
    }
    const json = (await res.json()) as { data: ApplicationDocument };
    return json.data;
  },
  documentDownloadUrl: (id: string, documentId: string, download = false) =>
    `${API_BASE}/api/applications/${id}/documents/${documentId}/download${
      download ? "?download=1" : ""
    }`,
  deleteDocument: (id: string, documentId: string) =>
    api.delete<{ id: string; deleted: boolean }>(
      `/api/applications/${id}/documents/${documentId}`,
    ),

  reviewDocument: (
    id: string,
    documentId: string,
    body: { status: DocumentReviewStatus; note?: string | null },
  ) =>
    api.patch<ApplicationDocument>(
      `/api/applications/${id}/documents/${documentId}/review`,
      body,
    ),

  sendUploadLink: (
    id: string,
    body: { email: string; customMessage?: string | null },
  ) =>
    api.post<SendUploadLinkResult>(
      `/api/applications/${id}/send-upload-link`,
      body,
    ),
  resendUploadLink: (id: string) =>
    api.post<SendUploadLinkResult>(
      `/api/applications/${id}/resend-upload-link`,
      {},
    ),
  getLatestUploadLink: (id: string) =>
    api.get<ApplicationUploadLinkSummary | null>(
      `/api/applications/${id}/upload-link`,
    ),
  /** SEND_LINK flow — emails a self-serve form link; creates NO application. */
  sendApplicationLink: (body: SendApplicationLinkInput) =>
    api.post<SendApplicationLinkResult>("/api/applications/send-link", body),
};

// ─── Public (token-gated) application API ──────────────────────────────────
// Used by the merchant-facing /upload/:appId/:token page. No auth cookies are
// expected; the 6-char token + appId pair acts as the capability.

async function publicRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    // Explicitly omit credentials so the browser does not attach the agent's
    // session cookies to the merchant-facing flow.
    credentials: "omit",
  });
  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }
  if (!res.ok) {
    const err = (
      payload as {
        error?: { type?: string; message?: string; details?: unknown };
      } | null
    )?.error;
    throw new ApiError(
      res.status,
      err?.message ?? `Request failed (${res.status})`,
      err?.type,
      err?.details,
    );
  }
  if (payload && typeof payload === "object" && "data" in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

export const publicApplicationsApi = {
  getApplication: (id: string, token: string) =>
    publicRequest<PublicApplicationPayload>(
      `/api/public/applications/${encodeURIComponent(id)}/${encodeURIComponent(token)}`,
    ),
  uploadDocument: async (
    id: string,
    token: string,
    docType: string,
    file: File,
    options?: { recaptchaToken?: string | null },
  ) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("docType", docType);
    if (options?.recaptchaToken)
      fd.append("recaptchaToken", options.recaptchaToken);
    const headers: Record<string, string> = {};
    if (options?.recaptchaToken)
      headers["x-recaptcha-token"] = options.recaptchaToken;
    return publicRequest<PublicApplicationDocument>(
      `/api/public/applications/${encodeURIComponent(id)}/${encodeURIComponent(token)}/documents/upload`,
      { method: "POST", body: fd, headers },
    );
  },
  submit: (
    id: string,
    token: string,
    options?: { recaptchaToken?: string | null },
  ) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (options?.recaptchaToken)
      headers["x-recaptcha-token"] = options.recaptchaToken;
    return publicRequest<{ status: UploadLinkStatus; submittedAt: string }>(
      `/api/public/applications/${encodeURIComponent(id)}/${encodeURIComponent(token)}/submit`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          recaptchaToken: options?.recaptchaToken ?? null,
        }),
      },
    );
  },
};

// ─── Public application form links (SEND_LINK flow) ────────────────────────
// Used by the merchant-facing /onboard/:linkId/:token page. The link id +
// token pair is the capability; the application is created on submit.

export const publicApplicationLinksApi = {
  getLink: (linkId: string, token: string) =>
    publicRequest<PublicFormLinkPayload>(
      `/api/public/application-links/${encodeURIComponent(linkId)}/${encodeURIComponent(token)}`,
    ),
  submit: (
    linkId: string,
    token: string,
    formData: Record<string, unknown>,
    options?: { recaptchaToken?: string | null },
  ) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (options?.recaptchaToken)
      headers["x-recaptcha-token"] = options.recaptchaToken;
    return publicRequest<{ submitted: boolean; applicationId: string }>(
      `/api/public/application-links/${encodeURIComponent(linkId)}/${encodeURIComponent(token)}/submit`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ formData }),
      },
    );
  },
  // Attach a document to the application created by `submit` — the merchant
  // uploads these right after submitting, keyed on the same link id + token.
  uploadDocument: async (
    linkId: string,
    token: string,
    docType: string,
    file: File,
    options?: { recaptchaToken?: string | null },
  ) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("docType", docType);
    if (options?.recaptchaToken)
      fd.append("recaptchaToken", options.recaptchaToken);
    const headers: Record<string, string> = {};
    if (options?.recaptchaToken)
      headers["x-recaptcha-token"] = options.recaptchaToken;
    return publicRequest<PublicApplicationDocument>(
      `/api/public/application-links/${encodeURIComponent(linkId)}/${encodeURIComponent(token)}/documents/upload`,
      { method: "POST", body: fd, headers },
    );
  },
};

// ─── Merchants ───────────────────────────────────────────────────────────────

export interface MerchantListItem {
  id: number;
  merchantId: string;
  businessName: string;
  dbaName: string | null;
  processor: string;
  status: string;
  assignedAgentUserId: string | null;
  assignedAgent: UserRef | null;
  applicationId: string | null;
  totalLifetimeVolume: string;
  totalLifetimeTransactions: number;
  previousDayVolume: number;
  previousDayTransactionCount: number;
  updatedAt: string;
}

export interface MerchantDetail {
  id: number;
  merchantId: string;
  businessName: string;
  dbaName: string | null;
  processor: string;
  status: string;
  riskClassification: string | null;
  agentName: string | null;
  subAgentName: string | null;
  cardAcceptanceMethod: string | null;
  processorData: Record<string, unknown> | null;
  mccCode: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressZip: string | null;
  totalLifetimeVolume: number;
  totalLifetimeTransactions: number;
  previousDayVolume: number;
  previousDayTransactionCount: number;
  applicationId: string | null;
  assignedAgentUserId: string | null;
  assignedAgent: UserRef | null;
  /** The contact email on the originating application; merchants have no email of their own. */
  contactEmail: string | null;
  /** Issuing (spend site) merchant — its wallet/corpay/cards live in Issuing. */
  isIssuing?: boolean;
  issuingReference?: string | null;
  /** Present once an issuing application is approved; gates the org tabs. */
  workosOrgId?: string | null;
  /** Card types chosen at issuing onboarding; pre-fills the Corpay attach form. */
  issuingAllowedCardTypes?: Array<"VIRTUAL" | "PHYSICAL" | "GHOST">;
  application: {
    id: string;
    applicationType: ApplicationType;
    stage: string;
    createdBy: UserRef | null;
    assignedAgent: UserRef | null;
  } | null;
  batches: { date: string; volume: number; count: number }[];
  createdAt: string;
  updatedAt: string;
}

export interface MerchantsListParams {
  search?: string;
  status?: string[];
  processor?: string[];
  /** INTERNAL-only: filter to merchants assigned to any of these user ids. */
  assignedAgentUserId?: string[];
  sort?: string;
  page?: number;
  limit?: number;
}

function buildMerchantsQuery(params: MerchantsListParams): string {
  const sp = new URLSearchParams();
  if (params.search) sp.set("search", params.search);
  if (params.sort) sp.set("sort", params.sort);
  if (params.page) sp.set("page", String(params.page));
  if (params.limit) sp.set("limit", String(params.limit));
  for (const id of params.assignedAgentUserId ?? [])
    sp.append("assignedAgentUserId", id);
  for (const s of params.status ?? []) sp.append("status", s);
  for (const p of params.processor ?? []) sp.append("processor", p);
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export interface MerchantsListResponse {
  data: MerchantListItem[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export const merchantsApi = {
  // Keeps the `{ data, meta }` envelope so the list UI can show `meta.total`.
  list: (params: MerchantsListParams = {}) =>
    requestEnvelope<MerchantsListResponse>({
      path: `/api/merchants${buildMerchantsQuery(params)}`,
      method: "GET",
    }),
  get: (id: number | string) => api.get<MerchantDetail>(`/api/merchants/${id}`),
  assign: (id: number, body: { assignedAgentUserId: string | null }) =>
    api.patch<MerchantDetail>(`/api/merchants/${id}/assign`, body),
  disable: (id: number) =>
    api.patch<MerchantDetail>(`/api/merchants/${id}/disable`, {}),
  // Processor sync status (last sync time, status per processor).
  syncStatus: () => api.get<Processor[]>("/api/merchants/sync/status"),

  // The merchant's WorkOS organization. No Issuing calls anywhere here: that
  // service is in PCI scope and stays isolated, so wallet, cards and funding
  // live in the issuing portal behind its own perimeter. Who belongs to the
  // merchant and its Corpay identity are facts about the ORGANIZATION, so they
  // come from WorkOS instead.
  orgMembers: (id: number | string) =>
    api.get<OrgMember[]>(`/api/merchants/${id}/org/members`),
  orgCorpay: (id: number | string) =>
    api.get<CorpayView>(`/api/merchants/${id}/org/corpay`),
  saveOrgCorpay: (id: number | string, body: SaveCorpayInput) =>
    api.put<CorpayView>(`/api/merchants/${id}/org/corpay`, body),
};

export type CardType = "VIRTUAL" | "PHYSICAL" | "GHOST";

export interface OrgMember {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  roles: string[];
}

export interface CorpayView {
  attached: boolean;
  accountCode: string | null;
  customerId: string | null;
  cardTypes: CardType[];
}

export interface SaveCorpayInput {
  accountCode: string;
  customerId: string;
  cardTypes: CardType[];
}

// ─── Processors ──────────────────────────────────────────────────────────────

export interface Processor {
  id: number;
  processorName: string;
  processorCode: string;
  isActive: boolean;
  /**
   * True when the API has a registered adapter for this processor (sync
   * supported). Selectable-only processors (preferred-processor options
   * like Worldpay/Nexio) come back with hasAdapter=false.
   */
  hasAdapter?: boolean;
  /**
   * True when applications can be submitted to this processor via API
   * (Elavon/Valmar/Mentom). Manual-flow processors (EMS, Worldpay,
   * Priority, Nexio, Secure) come back false — internal moves those
   * applications stage by stage.
   */
  supportsSubmission?: boolean;
  syncFrequencyHours: number;
  lastSyncDate: string | null;
  syncStatus: string;
  configData: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/**
 * One EMS merchant category. Several share a 4-digit `MCC`, each with its own
 * risk profile — `Id` is what EMS wants on a deal, not `MCC`.
 */
export interface EmsMccOption {
  Id: number;
  MCC: string;
  Description: string;
  HighRisk?: boolean;
  /** Addendum forms EMS requires for this category, if any. */
  Addendums?: { ID: number }[] | null;
}

/** One Elavon extended MCC; `code` (e.g. "5999J") is what Elavon boards with. */
export interface ElavonMccOption {
  code: string;
  baseCode: string;
  description: string;
  industry: string;
}

/** One Elavon authorization pricing program, labelled as in SAT; "OTHER" means a custom code. */
export interface ElavonAuthPricingProgram {
  code: string;
  label: string;
}

/** EMS reference tables offered as dropdowns when boarding a deal. */
export type EmsLookupKind =
  | "location-types"
  | "zone-types"
  | "area-types"
  | "transaction-profiles"
  | "ownership-types"
  | "owner-titles"
  | "document-types"
  | "program-applications"
  | "gateway-options";

export interface EmsLookupOption {
  Id: number;
  Description: string;
}

/** A rep at EMS. `branch`/`repCode` are blank for most of them. */
export interface EmsSalesPerson {
  userName: string;
  email: string;
  repType: string;
  offices: string[];
  branch: string;
  repCode: string;
}

export const processorsApi = {
  list: () => api.get<Processor[]>("/api/processors"),
  /**
   * EMS's full merchant-category list. EMS has no server-side filter, so the
   * whole list comes back regardless — narrowing is a client concern.
   */
  emsMccs: () => api.get<EmsMccOption[]>("/api/processors/ems/mccs"),
  emsLookup: (kind: EmsLookupKind) =>
    api.get<EmsLookupOption[]>(`/api/processors/ems/lookups/${kind}`),
  emsSalesPeople: () =>
    api.get<EmsSalesPerson[]>("/api/processors/ems/sales-people"),
  elavonMccs: () => api.get<ElavonMccOption[]>("/api/processors/elavon/mccs"),
  elavonAuthPricingPrograms: () =>
    api.get<ElavonAuthPricingProgram[]>(
      "/api/processors/elavon/auth-pricing-programs",
    ),
};

// ─── EMS deal documents (generate → sign → submit) ───────────────────────────

export type EmsDocumentSetStatus =
  | "not_generated"
  | "generated"
  | "stale"
  | "awaiting_signature"
  | "declined"
  | "signed";

export interface EmsStoredDocument {
  emsDocumentId: number;
  name: string;
  signatureRequired: boolean;
  unsignedDocumentId: string;
}

export interface EmsDealDocumentsOverview {
  status: EmsDocumentSetStatus;
  generatedAt: string | null;
  sentAt: string | null;
  signedAt: string | null;
  declinedAt: string | null;
  docusealTemplateId: number | null;
  docusealSubmissionId: number | null;
  documents: EmsStoredDocument[];
}

export interface EmsSendSigner {
  email: string;
  name?: string | null;
  role?: string | null;
}

export const emsDealDocumentsApi = {
  overview: (applicationId: string) =>
    api.get<EmsDealDocumentsOverview>(
      `/api/applications/${applicationId}/ems/documents`,
    ),
  generate: (applicationId: string) =>
    api.post<{ documents: EmsStoredDocument[] }>(
      `/api/applications/${applicationId}/ems/documents/generate`,
      {},
    ),
  createTemplate: (applicationId: string) =>
    api.post<{ templateId: number; created: boolean }>(
      `/api/applications/${applicationId}/ems/documents/template`,
      {},
    ),
  send: (
    applicationId: string,
    body: { signers: EmsSendSigner[]; notes?: string | null },
  ) =>
    api.post<{ submissionId: number | null; signers: DocusealSigner[] }>(
      `/api/applications/${applicationId}/ems/documents/send`,
      body,
    ),
};

// ─── Reporting (pipeline analytics over applications + stage events) ──────────

export interface FunnelRow {
  stage: string;
  count: number;
}
export interface StageMovementRow {
  stage: string;
  movedIn: number;
}
export interface StageVelocityRow {
  stage: string;
  avgHours: number | null;
  samples: number;
}
export interface BottleneckRow {
  stage: string;
  overdue: number;
  total: number;
  baselineHours: number | null;
}
export interface StatusRollupRow {
  date: string;
  created: number;
}

export interface ReportingDateParams {
  from?: string;
  to?: string;
}

export const reportingApi = {
  funnel: (params: ReportingDateParams = {}) =>
    api.get<FunnelRow[]>(
      `/api/reporting/funnel${qs(params as Record<string, string | undefined>)}`,
    ),
  stageMovements: (params: ReportingDateParams = {}) =>
    api.get<StageMovementRow[]>(
      `/api/reporting/stage-movements${qs(params as Record<string, string | undefined>)}`,
    ),
  stageVelocity: (sinceDays?: number) =>
    api.get<StageVelocityRow[]>(
      `/api/reporting/stage-velocity${sinceDays ? `?sinceDays=${sinceDays}` : ""}`,
    ),
  bottlenecks: () => api.get<BottleneckRow[]>("/api/reporting/bottlenecks"),
  statusRollup: (days?: number) =>
    api.get<StatusRollupRow[]>(
      `/api/reporting/status-rollup${days ? `?days=${days}` : ""}`,
    ),
  teamActivity: () =>
    api.get<{ configured: boolean; rows: unknown[] }>(
      "/api/reporting/team-activity",
    ),
};

// ─── Dashboard (KPI cards + processing-volume chart) ─────────────────────────

export interface DashboardCounts {
  preVet: number;
  documentCollection: number;
  pended: number;
  approved: number;
  deployed: number;
  declined: number;
  notProcessing: number;
}

export interface DashboardChartPoint {
  period: string;
  volume: number;
  count: number;
  applications: number;
}

export interface DashboardStats {
  totalApplications: number;
  totalMerchants: number;
  openTickets: number;
}

export interface DashboardSummaryMetric {
  current: number;
  /** null when no date range is applied (all-time view has no comparison). */
  previous: number | null;
}

export interface DashboardSummary {
  applications: DashboardSummaryMetric;
  approved: DashboardSummaryMetric;
  deployed: DashboardSummaryMetric;
  volume: DashboardSummaryMetric;
  activeMerchants: DashboardSummaryMetric;
}

export type DashboardTimeRange =
  | "last_30_days"
  | "month_to_date"
  | "last_month"
  | "quarter_to_date"
  | "last_quarter"
  | "year_to_date"
  | "last_year";

export type RecentApplicationUserType =
  | "INTERNAL"
  | "AGENT"
  | "SUB_AGENT"
  | "GODADDY_MERCHANT";

export interface RecentApplicationUserRef {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  userType: RecentApplicationUserType;
}

export interface RecentApplication {
  id: string;
  appNumber: string;
  createdAt: string;
  stage: string;
  businessName: string;
  dbaName: string | null;
  mid: string | null;
  needsGateway: boolean;
  needsIssuing: boolean;
  needsAch: boolean;
  hasSubAgent: boolean;
  subAgent: RecentApplicationUserRef | null;
  assignedAgent: RecentApplicationUserRef | null;
  createdBy: RecentApplicationUserRef | null;
}

export interface DashboardDateRange {
  from?: string;
  to?: string;
}

function dashboardRangeQuery(range?: DashboardDateRange): string {
  const params = new URLSearchParams();
  if (range?.from) params.set("from", range.from);
  if (range?.to) params.set("to", range.to);
  const s = params.toString();
  return s ? `?${s}` : "";
}

export type DashboardGranularity = "daily" | "weekly" | "monthly" | "yearly";

function dashboardChartQuery(opts: {
  timeRange?: DashboardTimeRange;
  from?: string;
  to?: string;
  granularity?: DashboardGranularity;
}): string {
  const params = new URLSearchParams();
  if (opts.from && opts.to) {
    params.set("from", opts.from);
    params.set("to", opts.to);
    if (opts.granularity) params.set("granularity", opts.granularity);
  } else if (opts.timeRange) {
    params.set("timeRange", opts.timeRange);
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

/** Pulse's verdicts: the dashboard decides these processing conditions and the portal acts on them. */
export type VerdictKind =
  | "deployed_no_test"
  | "deploy_unconfirmed"
  | "live_never_processed"
  | "not_processing"
  | "gone_quiet"
  | "missed_usual_day"
  | "checkin_due"
  | "approved_not_live";

export type NeedsYouKind =
  | "stuck_in_stage"
  | "signature_without_signer"
  | "docs_outstanding"
  | "processor_attention"
  | "stopped_processing"
  | VerdictKind;

export interface NeedsYouItem {
  id: string;
  kind: NeedsYouKind;
  severity: "high" | "medium" | "low";
  title: string;
  detail: string | null;
  entityType: "application" | "merchant";
  entityId: string;
  href: string;
  firstSeenAt: string;
}

export interface NeedsYou {
  enabled: boolean;
  total: number;
  items: NeedsYouItem[];
}

export const needsYouApi = {
  list: (limit = 20) => api.get<NeedsYou>(`/api/needs-you?limit=${limit}`),
  snooze: (id: string, days: 1 | 3 | 7) => api.post<void>(`/api/needs-you/${id}/snooze`, { days }),
  resolve: (id: string) => api.post<void>(`/api/needs-you/${id}/resolve`),
  reopen: (id: string) => api.post<void>(`/api/needs-you/${id}/reopen`),
};

export type AlertChannel = "in_app" | "email" | "sms";

export interface AlertRecipient {
  id: string;
  userId: string | null;
  email: string | null;
  channels: AlertChannel[];
  frequency: "immediate" | "daily" | "weekly";
  sender: "noreply" | "self";
  smsState: "proposed" | "confirmed" | "opted_out" | null;
}

export interface AlertRule {
  id: string;
  entityType: "application" | "merchant";
  entityId: string;
  kind: NeedsYouKind;
  params: { days?: number; muted?: boolean };
  active: boolean;
  recipients: AlertRecipient[];
}

export interface NewAlertRule {
  entityType: "application" | "merchant";
  entityId: string;
  kind: NeedsYouKind;
  params: { days?: number; muted?: boolean };
  recipients: ({ userId?: string; email?: string } & Pick<AlertRecipient, "channels" | "frequency" | "sender">)[];
}

export const alertRulesApi = {
  list: (entityType: "application" | "merchant", entityId: string) =>
    api.get<AlertRule[]>(`/api/alert-rules?entityType=${entityType}&entityId=${encodeURIComponent(entityId)}`),
  create: (body: NewAlertRule) => api.post<AlertRule>("/api/alert-rules", body),
  remove: (id: string) => api.delete<void>(`/api/alert-rules/${id}`),
};

export type LoopChannel = "in_app" | "email_noreply" | "email_self" | "sms";
export type LoopPlaybook = "missing_docs" | "stuck_ladder" | "checkin" | "custom";
export type LoopStatus = "proposed" | "active" | "paused" | "stopped" | "declined";
export type SendStatus = "scheduled" | "needs_review" | "sending" | "sent" | "skipped" | "cancelled" | "failed";

export interface FollowUpItem {
  id: string;
  kind: NeedsYouKind;
  severity: "high" | "medium" | "low";
  title: string;
  detail: string | null;
  status: "open" | "snoozed" | "resolved" | "cleared";
  openedAt: string;
  snoozedUntil: string | null;
  clearedAt: string | null;
  stalled: { stage: string; stageLabel: string; days: number } | null;
}

export interface FollowUpSend {
  subject?: string | null;
  id: string;
  toRole: string;
  toLabel: string;
  channel: LoopChannel;
  scheduledFor: string;
  status: SendStatus;
  preview: string;
  note: string | null;
  sentAt: string | null;
  canEdit: boolean;
}

export interface FollowUpLoop {
  inboundReply: boolean;
  cc: string[];
  bcc: string[];
  approveEverySend: boolean;
  aiDraft: boolean;
  id: string;
  itemId: string | null;
  playbook: LoopPlaybook;
  recipient: string;
  channel: LoopChannel;
  templateKey: string;
  schedule: { every?: { n: number; unit: "days" | "weeks" | "months" }; business_days?: boolean };
  stop: { max_sends?: number; reply?: boolean; until?: string; for?: { n: number; unit: "days" | "weeks" | "months" } };
  mode: "manual" | "auto";
  status: LoopStatus;
  stopReason: string | null;
  firstSendReviewed: boolean;
  owner: { id: string; name: string };
  createdAt: string;
  canManage: boolean;
  canSetAuto: boolean;
  sends: FollowUpSend[];
}

export interface ContactAttemptRow {
  id: string;
  sendId: string | null;
  channel: string;
  text: string;
  status: string;
  providerId: string | null;
  repliedAt: string | null;
  createdAt: string;
}

export interface RecordLog {
  items: FollowUpItem[];
  loops: FollowUpLoop[];
  attempts: ContactAttemptRow[];
}

export const followUpsApi = {
  draft: (body: { entityType: "application" | "merchant"; entityId: string; recipient: "merchant" | "agent" | "self"; instruction: string }) => api.post<{ body: string }>("/api/alert-loops/draft", body),
  record: (entityType: "application" | "merchant", entityId: string) =>
    api.get<RecordLog>(`/api/alert-loops/records/${entityType}/${encodeURIComponent(entityId)}`),
  create: (body: {
    entityType: "application" | "merchant"; entityId: string; recipient: "merchant" | "agent" | "self"; channel: LoopChannel;
    templateKey: "custom.merchant_followup" | "custom.reminder"; body: string; cc?: string[]; bcc?: string[]; approveEverySend?: boolean; aiDraft?: boolean;
    schedule: { every: { n: number; unit: "days" | "weeks" }; business_days?: boolean }; stop: { max_sends: number; reply?: boolean }; mode: "manual";
  }) => api.post<{ id: string }>("/api/alert-loops/loops", body),
  confirm: (id: string) => api.post<void>(`/api/alert-loops/loops/${id}/confirm`),
  decline: (id: string) => api.post<void>(`/api/alert-loops/loops/${id}/decline`),
  pause: (id: string) => api.post<void>(`/api/alert-loops/loops/${id}/pause`),
  resume: (id: string) => api.post<void>(`/api/alert-loops/loops/${id}/resume`),
  stop: (id: string) => api.post<void>(`/api/alert-loops/loops/${id}/stop`, {}),
  setMode: (id: string, mode: "manual" | "auto", acknowledgeUntested?: boolean) =>
    api.patch<void>(`/api/alert-loops/loops/${id}/mode`, { mode, acknowledgeUntested }),
  editSend: (id: string, body: { preview?: string; scheduledFor?: string; status?: "skip" | "pause" | "resume" }) =>
    api.patch<unknown>(`/api/alert-loops/sends/${id}`, body),
  cancelSend: (id: string, note: string) =>
    api.delete<void>(`/api/alert-loops/sends/${id}?note=${encodeURIComponent(note)}`),
};

/** The Acknowledge link in a no-reply follow-up email: public, the signed token is the key. */
export const acknowledgeApi = {
  view: (token: string) => api.get<{ business: string | null; acknowledgedAt: string | null }>(`/api/public/acknowledge/${encodeURIComponent(token)}`),
  acknowledge: (token: string) => api.post<{ acknowledgedAt: string }>(`/api/public/acknowledge/${encodeURIComponent(token)}`),
};

/** Pulse's picture of a merchant (kept in the dashboard): pattern, quiet-days threshold and deploy state. */
export interface MerchantHealth {
  merchantRef: string;
  name: string;
  pattern: { activeDays: number; expectedGap: number | null; label: string | null; recommendedThreshold: number | null; expectedBy: string | null };
  threshold: { days: number; set: number | null; setBy: string | null; setAt: string | null; goneQuietDays: number; suggestion: number | null };
  deploy: {
    deployedAt: string | null;
    markedBy: { name: string | null; kind: string | null; at: string } | null;
    firstTransaction: string | null;
    state: "confirmed" | "waived" | null;
    minVolume: number | null;
    setBy: string | null;
    setAt: string | null;
    note: string | null;
  };
}
export interface MonitoringPatch {
  thresholdDays?: number | null;
  deploy?: { state: "confirmed" | "waived" | null; minVolume?: number | null; note?: string | null };
}

export const monitoringApi = {
  get: (merchantId: number) => api.get<{ health: MerchantHealth | null }>(`/api/merchant-monitoring/${merchantId}`),
  save: (merchantId: number, patch: MonitoringPatch) => api.put<{ health: MerchantHealth }>(`/api/merchant-monitoring/${merchantId}`, patch),
};

export const dashboardApi = {
  counts: (range?: DashboardDateRange) =>
    api.get<DashboardCounts>(
      `/api/dashboard/counts${dashboardRangeQuery(range)}`,
    ),
  stats: () => api.get<DashboardStats>("/api/dashboard/stats"),
  summary: (range?: DashboardDateRange) =>
    api.get<DashboardSummary>(
      `/api/dashboard/summary${dashboardRangeQuery(range)}`,
    ),
  chart: (opts: {
    timeRange?: DashboardTimeRange;
    from?: string;
    to?: string;
    granularity?: DashboardGranularity;
  }) =>
    api.get<DashboardChartPoint[]>(
      `/api/dashboard/chart${dashboardChartQuery(opts)}`,
    ),
  recentApplications: (range?: DashboardDateRange) =>
    api.get<RecentApplication[]>(
      `/api/dashboard/recent-applications${dashboardRangeQuery(range)}`,
    ),
};

// ─── Home (the new internal Home's one read; mirrors modules/home/home.service.ts) ───

export interface HomeSeriesDay {
  day: string;
  volume: number;
  transactions: number;
  merchants: number;
}

export interface HomeHealthMerchant {
  merchantId: string;
  name: string;
  /** Days since the last batch; null = never processed. */
  daysSince: number | null;
  lastBatchDay: string | null;
}

export interface HomeResidualMonth {
  month: string;
  commission: number;
  msdProfit: number;
  volume: number;
  deals: number;
  /** The month's least settled commission report: draft, finalized (approved) or paid. */
  status: "draft" | "finalized" | "paid";
}

export interface HomeOverview {
  generatedAt: string;
  numbers: {
    liveMerchants: number;
    processed7d: number;
    notProcessing: number;
    goneQuiet: number;
    neverProcessed: number;
    volume14d: number;
    transactions14d: number;
    volumePrior14d: number;
    baselineDaily: number;
    openApplications: number;
    newApplications30d: number;
    newApplicationsPrior30d: number;
    approvals30d: number;
    approvalsPrior30d: number;
    deploys30d: number;
    deploysPrior30d: number;
    openTickets: number;
  };
  series: HomeSeriesDay[];
  pipeline: { stage: string; label: string; count: number }[];
  topMerchants: { merchantId: string; name: string; volume14d: number; prior14d: number }[];
  health: { notProcessing: HomeHealthMerchant[]; goneQuiet: HomeHealthMerchant[]; neverProcessed: HomeHealthMerchant[] };
  /** Finance and admin only; null for everyone else. */
  residuals: { months: HomeResidualMonth[] } | null;
}

export const homeApi = {
  overview: () => api.get<HomeOverview>("/api/home"),
};

// ─── Poynt (poynt-collect) ────────────────────────────────────────────────────

export interface PoyntCharge {
  id: string;
  amountCents: number;
  currency: string;
  status: string;
  poyntTransactionId: string | null;
  storeId: string | null;
  createdAt: string;
}

export interface PoyntChargeInput {
  amountCents: number;
  currency?: string;
  storeId?: string;
  nonce?: string;
}

export const poyntApi = {
  status: () => api.get<{ configured: boolean }>("/api/poynt/status"),
  stores: () =>
    api.get<{ configured: boolean; stores: unknown[] }>("/api/poynt/stores"),
  charge: (body: PoyntChargeInput) =>
    api.post<
      | { configured: false; message: string }
      | { configured: true; chargeId: string; status: string; message: string }
    >("/api/poynt/charge", body),
  listCharges: () => api.get<PoyntCharge[]>("/api/poynt/charges"),
};

// ─── Helpdesk / Tickets ───────────────────────────────────────────────────

export type TicketStatus = "OPEN" | "IN_PROGRESS" | "RESOLVED" | "CLOSED";
export type TicketPriority = "LOW" | "MEDIUM" | "HIGH" | "URGENT";
export type TicketCategory =
  | "GENERAL_INQUIRY"
  | "RISK_UNDERWRITING"
  | "TRANSACTIONS_SETTLEMENT"
  | "TECHNICAL_SUPPORT"
  | "SALES_SUPPORT";
export type TicketWorkflowStage =
  | "NEW"
  | "IN_PROGRESS"
  | "ADDITIONAL_INFO_REQUIRED"
  | "RESOLVED";

export interface Ticket {
  id: string;
  number: number;
  title: string;
  description: string | null;
  status: TicketStatus;
  priority: TicketPriority;
  category: TicketCategory;
  workflowStage: TicketWorkflowStage;
  merchantId: number | null;
  merchant: {
    id: number;
    merchantId: string;
    businessName: string;
    dbaName: string | null;
  } | null;
  createdById: string;
  createdBy: UserRef | null;
  assignedToId: string | null;
  assignedTo: UserRef | null;
  slaBreachDate: string | null;
  lastActivityDate: string;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListTicketsParams {
  status?: TicketStatus;
  priority?: TicketPriority;
  category?: TicketCategory;
  merchantId?: number;
  assignedToId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface CreateTicketInput {
  title: string;
  description?: string | null;
  priority?: TicketPriority;
  category?: TicketCategory;
  merchantId?: number | null;
  assignedToId?: string | null;
}

export interface UpdateTicketInput {
  title?: string;
  description?: string | null;
  status?: TicketStatus;
  priority?: TicketPriority;
  category?: TicketCategory;
  workflowStage?: TicketWorkflowStage;
  assignedToId?: string | null;
  merchantId?: number | null;
}

export interface TicketMetrics {
  total: number;
  slaBreached: number;
  avgResolutionHours: number | null;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  byCategory: Record<string, number>;
}

export interface TicketAttachment {
  id: string;
  ticketId: string;
  messageId: string | null;
  fileName: string;
  storageKey: string;
  contentType: string | null;
  sizeBytes: number | null;
  uploadedById: string | null;
  createdAt: string;
}

export const ticketsApi = {
  list: (params: ListTicketsParams = {}) =>
    api.get<Ticket[]>(
      `/api/tickets${qs(params as Record<string, string | number | undefined>)}`,
    ),
  metrics: () => api.get<TicketMetrics>("/api/tickets/metrics"),
  get: (id: string) => api.get<Ticket>(`/api/tickets/${id}`),
  create: (body: CreateTicketInput) => api.post<Ticket>("/api/tickets", body),
  update: (id: string, body: UpdateTicketInput) =>
    api.patch<Ticket>(`/api/tickets/${id}`, body),
  remove: (id: string) =>
    api.delete<{ id: string; deleted: boolean }>(`/api/tickets/${id}`),

  // Attachments
  listAttachments: (ticketId: string) =>
    api.get<TicketAttachment[]>(`/api/tickets/${ticketId}/attachments`),
  attachmentDownloadUrl: (ticketId: string, attachmentId: string) =>
    `${API_BASE}/api/tickets/${ticketId}/attachments/${attachmentId}/download`,
  uploadAttachment: async (
    ticketId: string,
    file: File,
    messageId?: string,
  ) => {
    const form = new FormData();
    form.append("file", file);
    if (messageId) form.append("messageId", messageId);
    const res = await fetch(`${API_BASE}/api/tickets/${ticketId}/attachments`, {
      method: "POST",
      credentials: "include",
      body: form,
    });
    if (!res.ok) throw new Error(`Upload failed (${res.status})`);
    return (await res.json()) as { data: TicketAttachment };
  },
};

// ─── Ticket messages (conversation thread) ────────────────────────────────

export interface TicketMessage {
  id: string;
  ticketId: string;
  body: string;
  isPrivate: boolean;
  author: UserRef | null;
  createdAt: string;
  updatedAt: string;
}

export const ticketMessagesApi = {
  list: (ticketId: string) =>
    api.get<TicketMessage[]>(`/api/tickets/${ticketId}/messages`),
  create: (ticketId: string, body: string, isPrivate = false) =>
    api.post<TicketMessage>(`/api/tickets/${ticketId}/messages`, {
      body,
      isPrivate,
    }),
  remove: (ticketId: string, messageId: string) =>
    api.delete<{ id: string; deleted: boolean }>(
      `/api/tickets/${ticketId}/messages/${messageId}`,
    ),
};

// ─── Merchant notes ───────────────────────────────────────────────────────

export type MerchantNoteVisibility = "PRIVATE" | "INTERNAL" | "PUBLIC";

export interface MerchantNote {
  id: string;
  merchantId: number;
  authorId: string;
  author: UserRef | null;
  body: string;
  visibility: MerchantNoteVisibility;
  createdAt: string;
}

export const merchantNotesApi = {
  list: (merchantId: number) =>
    api.get<MerchantNote[]>(`/api/merchants/${merchantId}/notes`),
  create: (
    merchantId: number,
    body: string,
    visibility: MerchantNoteVisibility = "PRIVATE",
  ) =>
    api.post<MerchantNote>(`/api/merchants/${merchantId}/notes`, {
      body,
      visibility,
    }),
  remove: (merchantId: number, noteId: string) =>
    api.delete<{ id: string; deleted: boolean }>(
      `/api/merchants/${merchantId}/notes/${noteId}`,
    ),
};

// ─── Application notes (conversation thread) ──────────────────────────────

export type ApplicationNoteVisibility = "PRIVATE" | "INTERNAL" | "PUBLIC";

export interface ApplicationNote {
  id: string;
  applicationId: string;
  authorId: string;
  author: UserRef | null;
  body: string;
  visibility: ApplicationNoteVisibility;
  createdAt: string;
}

export const applicationNotesApi = {
  list: (applicationId: string) =>
    api.get<ApplicationNote[]>(`/api/applications/${applicationId}/notes`),
  create: (
    applicationId: string,
    body: string,
    visibility: ApplicationNoteVisibility = "PRIVATE",
  ) =>
    api.post<ApplicationNote>(`/api/applications/${applicationId}/notes`, {
      body,
      visibility,
    }),
  update: (
    applicationId: string,
    noteId: string,
    body: { body?: string; visibility?: ApplicationNoteVisibility },
  ) =>
    api.patch<ApplicationNote>(
      `/api/applications/${applicationId}/notes/${noteId}`,
      body,
    ),
  remove: (applicationId: string, noteId: string) =>
    api.delete<{ id: string; deleted: boolean }>(
      `/api/applications/${applicationId}/notes/${noteId}`,
    ),
};

// ─── AI Call Log ──────────────────────────────────────────────────────────

/** Display status for a call: in-progress, or the derived ended outcome. */
export type AICallOutcome =
  | "IN_PROGRESS"
  | "COMPLETED"
  | "NO_ANSWER"
  | "FAILED";

export interface AICallLogEntry {
  id: number;
  createdAt: string;
  fullName: string | null;
  phoneNumber: string | null;
  email: string | null;
  retellCallAnalysis: string | null;
  userSentiment: string | null;
  followupEmailRequested: string | null;
  transcript: string | null;
  /** Call participants + metadata, surfaced in the detail view. */
  agentName: string | null;
  direction: string | null;
  durationMs: string | null;
  disconnectionReason: string | null;
  /** Raw Retell lifecycle status (ongoing/ended/…). */
  callStatus: string | null;
  /** Derived display outcome (server-computed from status + disconnection). */
  outcome: AICallOutcome;
}

export interface AICallLogListResponse {
  data: AICallLogEntry[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export interface ListAICallLogParams {
  page?: number;
  limit?: number;
  searchTerm?: string;
  sort?: string;
  /** Exact stored sentiment values, e.g. ["Positive", "Neutral"]. */
  sentiment?: string[];
}

export const aiCallLogApi = {
  // Bypasses the `data`-unwrapping `api.get` helper because the v2 UI needs
  // the `meta.total` for server-side pagination.
  list: async (
    params: ListAICallLogParams = {},
  ): Promise<AICallLogListResponse> => {
    const sp = new URLSearchParams();
    if (params.page) sp.set("page", String(params.page));
    if (params.limit) sp.set("limit", String(params.limit));
    if (params.searchTerm) sp.set("searchTerm", params.searchTerm);
    if (params.sort) sp.set("sort", params.sort);
    for (const s of params.sentiment ?? []) sp.append("sentiment", s);
    const qs = sp.toString() ? `?${sp.toString()}` : "";

    const res = await fetch(`${API_URL}/api/ai-call-log${qs}`, {
      credentials: "include",
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) {
      const err = (
        payload as {
          error?: { message?: string; type?: string; details?: unknown };
        } | null
      )?.error;
      throw new ApiError(
        res.status,
        err?.message ?? `Request failed (${res.status})`,
        err?.type,
        err?.details,
      );
    }
    return payload as AICallLogListResponse;
  },
  /** INTERNAL — pull historical calls from Retell on demand (new calls arrive
   *  via the webhook; this backfills / recovers any it missed). */
  sync: () =>
    api.post<{ synced: boolean; upserted: number; reason?: string }>(
      "/api/ai-call-log/sync",
      {},
    ),
};

// ─── Forms (dynamic intake) ──────────────────────────────────────────────────
// A form is either SECURE (single-use token links) or CAMPAIGN (rendered on a
// landing site via form id + per-form secret). Public access — schema AND intake
// — is authenticated by the per-form secret; secure links live at /secure/:token.

export type FormCallStatus =
  | "NONE"
  | "PENDING"
  | "PLACED"
  | "COMPLETED"
  | "NO_ANSWER"
  | "FAILED";

export interface RetellAgentOption {
  id: string;
  name: string;
}

export interface RetellAgentsResponse {
  /** False when RETELL_API_KEY isn't set on the server (dev/local). */
  configured: boolean;
  agents: RetellAgentOption[];
}

/** Where the "Email on submission" email is delivered in this environment. */
export interface SubmissionEmailRecipients {
  to: string[];
  bcc: string[];
}

export interface FormTestResult {
  submissionId: string;
  valid: boolean;
  errors: { key: string; message: string }[];
  callStatus: string;
}

export interface FieldSpec {
  id?: string;
  key: string;
  label: string;
  type:
    | "text"
    | "email"
    | "phone"
    | "number"
    | "date"
    | "textarea"
    | "select"
    | "checkbox"
    | "file"
    | "button";
  placeholder?: string;
  required?: boolean;
  encrypt?: boolean;
  options?: string[];
  color?: string;
  validation?: {
    maxLength?: number;
    minLength?: number;
    pattern?: string;
    min?: number;
    max?: number;
    country?: string;
    maxFiles?: number;
    maxSizeMb?: number;
  };
}

export interface FormStyleDto {
  kind: "solid" | "gradient";
  preset: string;
  custom?: { background: string; isDark: boolean };
  /** Preferred rendered card width, applied by the embedding campaign. */
  width?: "sm" | "md" | "lg" | "xl" | "full";
}

export interface FormDto {
  id: string;
  name: string;
  slug: string;
  description: string;
  category: "SECURE" | "CAMPAIGN";
  fieldSchema: FieldSpec[];
  style: FormStyleDto | null;
  consentFieldKeys: string[];
  requireRecaptcha: boolean;
  isActive: boolean;
  callEnabled: boolean;
  retellAgentId: string | null;
  fromNumber: string | null;
  callDelaySecs: number;
  notifyOnSubmit: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: { submissions: number; links: number };
}

export interface FormWithSecret extends FormDto {
  /** Plaintext secret — returned exactly once, at creation. */
  secret: string;
}

export interface CreateFormBody {
  name: string;
  description?: string;
  category?: "SECURE" | "CAMPAIGN";
  fieldSchema?: FieldSpec[];
  style?: FormStyleDto;
  consentFieldKeys?: string[];
  requireRecaptcha?: boolean;
  isActive?: boolean;
  callEnabled?: boolean;
  retellAgentId?: string | null;
  fromNumber?: string | null;
  callDelaySecs?: number;
  notifyOnSubmit?: boolean;
}

export interface FormSubmissionRow {
  id: string;
  submitterName: string | null;
  submitterEmail: string | null;
  values: Record<string, unknown>;
  encryptedKeys: string[];
  valid: boolean;
  callStatus: FormCallStatus;
  retellCallId: string | null;
  sourceIp: string | null;
  submittedAt: string;
}

/** A file uploaded with a submission (secure-link intake only). */
export interface FormSubmissionDocumentRow {
  id: string;
  fieldKey: string;
  fileName: string;
  contentType: string | null;
  sizeBytes: number;
  createdAt: string;
}

/** Single-submission payload — the only place attachments are returned. */
export interface FormSubmissionWithDocuments {
  id: string;
  documents: FormSubmissionDocumentRow[];
}

export interface FormSubmissionsResponse {
  data: FormSubmissionRow[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

// ─── Secure form links ───────────────────────────────────────────────────────

export type FormLinkState = "active" | "submitted" | "expired" | "revoked";

export interface CreateLinkBody {
  /** When present, emails an invite to this address; otherwise just copy the URL. */
  recipientEmail?: string;
  message?: string;
  expiresInHours?: number;
}

/** Result of minting a link — includes the shareable URL + whether an email was sent. */
export interface FormLinkResult {
  id: string;
  token: string;
  linkUrl: string;
  recipientEmail: string | null;
  status: "PENDING" | "SUBMITTED" | "REVOKED";
  state: FormLinkState;
  expiresAt: string;
  createdAt: string;
  sent: boolean;
}

/** A row in a form's links list. linkUrl is non-null only while still usable. */
export interface FormLinkRow {
  id: string;
  recipientEmail: string | null;
  status: "PENDING" | "SUBMITTED" | "REVOKED";
  state: FormLinkState;
  linkUrl: string | null;
  expiresAt: string;
  submittedAt: string | null;
  revokedAt: string | null;
  submissionId: string | null;
  createdAt: string;
}

export const formsApi = {
  list: () => api.get<FormDto[]>("/api/forms"),
  get: (id: string) => api.get<FormDto>(`/api/forms/${id}`),
  retellAgents: () => api.get<RetellAgentsResponse>("/api/forms/retell-agents"),
  submissionEmailRecipients: () =>
    api.get<SubmissionEmailRecipients>(
      "/api/forms/submission-email-recipients",
    ),
  create: (body: CreateFormBody) =>
    api.post<FormWithSecret>("/api/forms", body),
  update: (id: string, body: Partial<CreateFormBody>) =>
    api.patch<FormDto>(`/api/forms/${id}`, body),
  remove: (id: string) => api.delete<{ deleted: boolean }>(`/api/forms/${id}`),
  rotateSecret: (id: string) =>
    api.post<{ secret: string }>(`/api/forms/${id}/rotate-secret`),
  /** In-app Test tool — runs the real intake pipeline (places a call if wired). */
  testSubmit: (id: string, values: Record<string, unknown>) =>
    api.post<FormTestResult>(`/api/forms/${id}/test`, values),
  submissions: async (
    id: string,
    params: {
      page?: number;
      limit?: number;
      valid?: "true" | "false";
      search?: string;
    } = {},
  ): Promise<FormSubmissionsResponse> => {
    const sp = new URLSearchParams();
    if (params.page) sp.set("page", String(params.page));
    if (params.limit) sp.set("limit", String(params.limit));
    if (params.valid) sp.set("valid", params.valid);
    if (params.search) sp.set("search", params.search);
    const qs = sp.toString() ? `?${sp.toString()}` : "";
    const res = await fetch(`${API_URL}/api/forms/${id}/submissions${qs}`, {
      credentials: "include",
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) {
      const err = (
        payload as { error?: { message?: string; type?: string } } | null
      )?.error;
      throw new ApiError(
        res.status,
        err?.message ?? `Request failed (${res.status})`,
        err?.type,
      );
    }
    return payload as FormSubmissionsResponse;
  },
  /** Decrypt this submission's encrypted fields on demand (eye toggle). */
  revealSubmission: (id: string, sid: string) =>
    api.post<{ values: Record<string, string> }>(
      `/api/forms/${id}/submissions/${sid}/reveal`,
    ),
  /** One submission, including its uploaded documents — the list omits them. */
  getSubmission: (id: string, sid: string) =>
    api.get<FormSubmissionWithDocuments>(`/api/forms/${id}/submissions/${sid}`),
  /** Absolute URL for a submission document; fetch it with credentials. */
  submissionDocumentUrl: (id: string, sid: string, docId: string) =>
    `${API_URL}/api/forms/${id}/submissions/${sid}/documents/${docId}/download`,
  listLinks: (formId: string) =>
    api.get<FormLinkRow[]>(`/api/forms/${formId}/links`),
  createLink: (formId: string, body: CreateLinkBody = {}) =>
    api.post<FormLinkResult>(`/api/forms/${formId}/links`, body),
  revokeLink: (formId: string, linkId: string) =>
    api.post<{ revoked: boolean }>(
      `/api/forms/${formId}/links/${linkId}/revoke`,
      {},
    ),
};

// ─── Public forms (by id + per-form secret) ───────────────────────────────────

export interface PublicFormSubmitResult {
  valid: boolean;
  submissionId?: string;
  errors?: { key: string; message: string }[];
  callStatus?: string;
}

/** Public render contract for a form — requires the per-form secret. */
export interface PublicFormSchema {
  id: string;
  name: string;
  description: string;
  style: FormStyleDto | null;
  fieldSchema: FieldSpec[];
  /** False when the form has been switched off (campaign closed). */
  isActive: boolean;
}

export const publicFormsApi = {
  schema: (formId: string, secret: string) =>
    publicRequest<PublicFormSchema>(
      `/api/public/forms/${encodeURIComponent(formId)}/schema`,
      {
        headers: { "x-form-secret": secret },
      },
    ),
  submit: (formId: string, secret: string, values: Record<string, unknown>) =>
    publicRequest<PublicFormSubmitResult>(
      `/api/public/forms/${encodeURIComponent(formId)}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-form-secret": secret,
        },
        body: JSON.stringify(values),
      },
    ),
  // Secret-less embed (iframe): render contract by id, reCAPTCHA-gated submit.
  embedSchema: (formId: string) =>
    publicRequest<PublicFormSchema>(
      `/api/public/forms/${encodeURIComponent(formId)}/embed`,
    ),
  embedSubmit: (
    formId: string,
    values: Record<string, unknown>,
    recaptchaToken?: string | null,
  ) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (recaptchaToken) headers["x-recaptcha-token"] = recaptchaToken;
    return publicRequest<PublicFormSubmitResult>(
      `/api/public/forms/${encodeURIComponent(formId)}/embed`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          ...values,
          recaptchaToken: recaptchaToken ?? null,
        }),
      },
    );
  },
};

/** Public payload for the token-gated secure form page (/secure/:token). */
export interface SecureFormLinkPayload {
  state: FormLinkState;
  recipientEmail: string | null;
  senderMessage: string | null;
  form: {
    name: string;
    description: string;
    style: FormStyleDto | null;
    fieldSchema: FieldSpec[];
  };
}

export const publicFormLinksApi = {
  get: (token: string) =>
    publicRequest<SecureFormLinkPayload>(
      `/api/public/form-links/${encodeURIComponent(token)}`,
    ),
  submit: (
    token: string,
    values: Record<string, unknown>,
    options?: {
      files?: { fieldKey: string; file: File }[];
      recaptchaToken?: string | null;
    },
  ) => {
    type Result = {
      valid: boolean;
      submissionId?: string;
      errors?: { key: string; message: string }[];
    };
    const url = `/api/public/form-links/${encodeURIComponent(token)}`;
    const files = options?.files ?? [];
    const recaptchaToken = options?.recaptchaToken;
    if (files.length > 0) {
      const fd = new FormData();
      for (const [k, v] of Object.entries(values)) {
        if (v !== undefined && v !== null)
          fd.append(k, v instanceof Date ? v.toISOString() : String(v));
      }
      for (const { fieldKey, file } of files) fd.append(fieldKey, file);
      if (recaptchaToken) fd.append("recaptchaToken", recaptchaToken);
      const headers: Record<string, string> = {};
      if (recaptchaToken) headers["x-recaptcha-token"] = recaptchaToken;
      return publicRequest<Result>(url, { method: "POST", body: fd, headers });
    }
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (recaptchaToken) headers["x-recaptcha-token"] = recaptchaToken;
    return publicRequest<Result>(url, {
      method: "POST",
      headers,
      body: JSON.stringify({
        ...values,
        recaptchaToken: recaptchaToken ?? null,
      }),
    });
  },
};
// ─── Document Library ────────────────────────────────────────────────────────

export interface DocumentCategoryDto {
  id: number;
  uid: string;
  name: string;
  slug: string;
  description: string | null;
  iconName: string | null;
  displayOrder: number;
  isActive: boolean;
}

export interface DocumentSubcategoryDto {
  id: number;
  uid: string;
  categoryId: number;
  name: string;
  slug: string;
  description: string | null;
  displayOrder: number;
  isActive: boolean;
}

export interface DocumentDto {
  id: number;
  uid: string;
  categoryId: number;
  subcategoryId: number | null;
  title: string;
  description: string | null;
  fileName: string;
  filePath: string;
  fileSize: number | null;
  fileType: string;
  mimeType: string | null;
  versionNumber: number;
  isLatestVersion: boolean;
  tags: string[] | null;
  downloadCount: number;
  isFeatured: boolean;
  requiresInternalAccess: boolean;
  isDeleted: boolean;
  createdDate: string;
  modifiedDate: string;
  category: DocumentCategoryDto;
  subcategory: DocumentSubcategoryDto | null;
}

export interface DocumentVersionDto {
  id: number;
  uid: string;
  documentId: number;
  versionNumber: number;
  fileName: string;
  filePath: string;
  fileSize: number | null;
  uploadNotes: string | null;
  uploadedByUserId: string | null;
  createdDate: string;
}

export interface ListDocumentsParams {
  categoryId?: number;
  subcategoryId?: number;
  search?: string;
}

export interface CreateDocumentInput {
  file: File;
  categoryId: number;
  subcategoryId?: number;
  title: string;
  description?: string;
  tags?: string[];
  requiresInternalAccess?: boolean;
  isFeatured?: boolean;
}

export interface UpdateDocumentInput {
  categoryId: number;
  subcategoryId?: number | null;
  title: string;
  description?: string | null;
  tags?: string[];
  requiresInternalAccess: boolean;
  isFeatured: boolean;
}

// Posts multipart/form-data and unwraps the `{ data }` envelope.
async function postDocumentForm(
  path: string,
  fd: FormData,
): Promise<DocumentDto> {
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  if (!res.ok) {
    const payload = (await res.json().catch(() => null)) as {
      error?: { message?: string };
    } | null;
    throw new ApiError(
      res.status,
      payload?.error?.message ?? `Request failed (${res.status})`,
    );
  }
  const json = (await res.json()) as { data: DocumentDto };
  return json.data;
}

export const documentsApi = {
  listCategories: () =>
    api.get<DocumentCategoryDto[]>("/api/documents/categories"),
  listSubcategories: (categoryId?: number) =>
    api.get<DocumentSubcategoryDto[]>(
      `/api/documents/subcategories${categoryId ? `?categoryId=${categoryId}` : ""}`,
    ),
  listDocuments: (params: ListDocumentsParams = {}) => {
    const sp = new URLSearchParams();
    if (params.categoryId) sp.set("categoryId", String(params.categoryId));
    if (params.subcategoryId)
      sp.set("subcategoryId", String(params.subcategoryId));
    if (params.search) sp.set("search", params.search);
    const qs = sp.toString() ? `?${sp.toString()}` : "";
    return api.get<DocumentDto[]>(`/api/documents${qs}`);
  },
  listFeatured: () => api.get<DocumentDto[]>("/api/documents/featured"),
  // The browser hits this GET directly (cookie auth); the endpoint streams the
  // file and bumps the download count server-side.
  downloadUrl: (uid: string) => `${API_URL}/api/documents/${uid}/download`,

  // Admin / write (INTERNAL-only on the server).
  listAll: (includeDeleted = true) =>
    api.get<DocumentDto[]>(
      `/api/documents/all?includeDeleted=${includeDeleted}`,
    ),
  listVersions: (uid: string) =>
    api.get<DocumentVersionDto[]>(`/api/documents/${uid}/versions`),
  // Direct browser GET (cookie auth) — streams a specific archived version.
  versionDownloadUrl: (versionUid: string) =>
    `${API_URL}/api/documents/versions/${versionUid}/download`,
  uploadDocument: (input: CreateDocumentInput) => {
    const fd = new FormData();
    fd.append("file", input.file);
    fd.append("categoryId", String(input.categoryId));
    if (input.subcategoryId)
      fd.append("subcategoryId", String(input.subcategoryId));
    fd.append("title", input.title);
    if (input.description) fd.append("description", input.description);
    if (input.tags) fd.append("tags", JSON.stringify(input.tags));
    fd.append(
      "requiresInternalAccess",
      String(input.requiresInternalAccess ?? false),
    );
    fd.append("isFeatured", String(input.isFeatured ?? false));
    return postDocumentForm("/api/documents", fd);
  },
  uploadVersion: (uid: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return postDocumentForm(`/api/documents/${uid}/versions`, fd);
  },
  updateDocument: (uid: string, body: UpdateDocumentInput) =>
    api.patch<DocumentDto>(`/api/documents/${uid}`, body),
  deleteDocument: (uid: string) =>
    api.delete<{ uid: string; deleted: boolean }>(`/api/documents/${uid}`),
};

export const accessRequestApi = {
  create: (body: {
    email: string;
    firstName: string;
    lastName: string;
    reason: string;
  }) =>
    api.post<{ id: string; status: AccessRequestStatus; createdAt: string }>(
      "/api/access-requests",
      body,
    ),
  list: (status?: AccessRequestStatus) =>
    api.get<AccessRequest[]>(
      `/api/access-requests${status ? `?status=${status}` : ""}`,
    ),
  approve: (id: string, body: { userType: UserType; roles?: string[] }) =>
    api.post<{
      request: { id: string; status: AccessRequestStatus };
      user: { id: string; email: string };
    }>(`/api/access-requests/${id}/approve`, body),
  deny: (id: string, body: { reason?: string }) =>
    api.post<{ id: string; status: AccessRequestStatus }>(
      `/api/access-requests/${id}/deny`,
      body,
    ),
};

// ─── Commissions (v3 rebuild — INTERNAL-only; design doc:
//     docs/plans/commissions_rebuild_schema.md) ────────────────────────────

export interface CommissionReport {
  id: number;
  name: string;
  processingMonth: string;
  datePaid: string | null;
  status: "draft" | "finalized" | "paid";
  notes: string | null;
  createdAt: string;
  createdBy?: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  // list shape
  importCount?: number;
  dealCount?: number;
  commissionTotal?: number;
  // detail shape
  imports?: CommissionImport[];
  commissions?: CommissionRow[];
  agentPayouts?: CommissionAgentPayout[];
  // Whether a negative deal costs the agent the whole net this month (COMMISSION_FULL_NEGATIVE_FROM).
  fullNegative?: boolean;
}

// Per-agent payout marking within a finalized report (Phase 3).
export interface StatementEmailResult {
  status: "sent" | "blocked" | "failed" | "suppressed";
  message?: string;
}

export interface StatementEmailBatch {
  sent: number;
  notSent: Array<StatementEmailResult & { agentEmail: string }>;
}

// One line of a report's activity: approvals and payments, sends, hand edits, imports, moves.
export interface ReportActivityEntry {
  id: number;
  at: string;
  kind: "report" | "payment" | "send" | "edit" | "import" | "access";
  text: string;
  reason: string | null;
  actor: string | null;
  agentEmail: string | null;
  // An agent-view send whose access is still open.
  revokeId: number | null;
}

// CC/BCC on agent statement emails; agentEmail null = every statement email.
export interface CommissionEmailCopy {
  id: number;
  agentEmail: string | null;
  email: string;
  kind: "cc" | "bcc";
}

export interface StatementEmailPreview {
  to: string;
  cc: string[];
  bcc: string[];
  subject: string;
  html: string;
  /** Why it can't go to the agent yet (not paid, statements off, no account); null when it can. */
  notSendable: string | null;
  lastSentAt: string | null;
}

export interface CommissionAgentPayout {
  id: number;
  reportId: number;
  agentEmail: string;
  status: "paid" | "pending";
  datePaid: string | null;
  commissionTotal: string;
  dealCount: number;
  paidByUserId: string | null;
  paidAt: string | null;
  notes: string | null;
}

export interface ProcessorDetectionCandidate {
  processor: string;
  headerMatch: boolean;
  signature: boolean;
  columnsMatched: number;
  columnsMapped: number;
  rows: number;
  knownMids: number;
  filenameHint: boolean;
  month: string | null;
}

export interface ProcessorDetection {
  suggested: string | null;
  confidence: "high" | "medium" | "low" | "none";
  candidates: ProcessorDetectionCandidate[];
  /** The hand-entry template: its deals are filed under the processor picked. */
  template?: boolean;
}

export interface CommissionImport {
  id: number;
  reportId: number | null;
  processor: string;
  region: string | null;
  processingMonth: string;
  filename: string;
  fileSize: number | null;
  status: "draft" | "review" | "finalized";
  mapping: Record<string, string> | null;
  datePaid: string | null;
  origin: string;
  rowCount: number | null;
  importedRowCount: number | null;
  skippedRowCount: number | null;
  totals: {
    volume: number;
    gross_profit: number;
    commission: number;
    msd_profit: number;
  } | null;
  notes: string | null;
  createdAt: string;
  commissions?: CommissionRow[];
  _count?: { commissions: number };
}

export interface CommissionPreviewRow {
  mid: string;
  merchantName: string;
  processingMonth: string;
  volume: number;
  transactions: number;
  grossProfit: number;
  chargebackCount: number;
  region: string | null;
  assignment: {
    agentEmail: string | null;
    merchantStatus: string | null;
  } | null;
  // Owner suggestion for an unresolved deal (closed merchant, closed status, or
  // the deal's most recent resolved row). null when resolved or no signal.
  suggestion: {
    kind: "agent" | "msd" | "terminated";
    agentEmail: string | null;
    source: "closed_name" | "status" | "history";
  } | null;
}

// Returned as `details.duplicate` on a 409 from the upload endpoint when the
// file's DATA (processor + month + MID set + gross total) already exists.
export interface CommissionDuplicateImport {
  importId: number;
  reportId: number | null;
  reportName: string | null;
  filename: string;
  createdAt: string;
  rows: number;
  /** The earlier import lives in the report being uploaded into. */
  sameReport: boolean;
  /** Replace is only offered when the earlier import can still be deleted. */
  canReplace: boolean;
  /** Deals in the earlier import that replacing would drop along with their hand-set figures / notes. */
  handEdited: number;
  internalNotes: number;
}

export function duplicateImportOf(
  err: unknown,
): CommissionDuplicateImport | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const d = err.details as
    | { code?: string; duplicate?: CommissionDuplicateImport }
    | undefined;
  return d?.code === "DUPLICATE_IMPORT" && d.duplicate ? d.duplicate : null;
}

export interface CommissionSourceFile {
  path: string;
  name: string;
  size: number;
  processors: string[];
}

export interface CommissionOwnerResolution {
  mid: string;
  merchantName?: string | null;
  kind: "agent" | "msd" | "terminated";
  agentEmail?: string | null;
}

export interface CommissionExpenseEntry {
  key: string;
  label: string;
  class: "real_oop" | "msd_kept" | "agent_only";
  basis: string;
  basisValue: number;
  rate: number;
  amount: number;
  ruleId: number;
  // Set when the basis is another deal's volume (e.g. Commonwealth's BIN on Plymouth Meeting).
  basisMid?: string;
}

/** One hand-set figure: what it was, what it is, and why. */
export interface CommissionOverride {
  from: unknown;
  to: unknown;
  reason: string;
  by: string;
  at: string;
}

export interface CommissionRow {
  id: number;
  importId: number;
  processor: string;
  mid: string;
  merchantName: string | null;
  processingMonth: string;
  agentEmail: string | null;
  homeType: string | null;
  // Termination MONTH (ISO 'YYYY-MM-DD' of the cycle a deal was terminated), or
  // null = historical/unknown. Matches the report's processing month ⇒ "terminated
  // this report" (red); otherwise historical (white).
  terminationReason: string | null;
  volume: string | null;
  transactions: number | null;
  grossProfit: string;
  chargebackCount: number;
  region: string | null;
  raw: Record<string, string>;
  rateRuleId: number | null;
  ruleScope: string | null;
  rateModel: string;
  rateValue: string | null;
  flatAmount: string | null;
  expenseBreakdown: CommissionExpenseEntry[];
  expenseTotal: string;
  netRevenue: string | null;
  commission: string;
  msdProfit: string | null;
  effectivePct: string | null;
  reviewStatus: "ok" | "needs_review";
  // What the agent's math says on a terminated deal; paid only when payTerminated is set.
  calculatedCommission: string | null;
  payTerminated: boolean;
  closeReason: string | null;
  overrides: Record<string, CommissionOverride> | null;
  // Staff-only; the agent statement endpoint never returns it.
  internalNote: string | null;
  // 'hidden' | 'shown' set by staff; null leaves it to the rule (terminated deals hide after their first month).
  agentVisibility: "hidden" | "shown" | null;
  // Whether the agent's statement leaves this deal out, by hand or by that rule.
  hiddenFromAgent?: boolean;
}

export interface CommissionRule {
  id: number;
  kind: "rate" | "expense" | "assignment" | "mapping";
  processor: string | null;
  agentEmail: string | null;
  mid: string | null;
  rateModel: string | null;
  rateValue: string | null;
  flatAmount: string | null;
  expenseKey: string | null;
  label: string | null;
  amountType: string | null;
  amount: string | null;
  expenseClass: string | null;
  appliesRegion: string | null;
  assignedAgentEmail: string | null;
  merchantName: string | null;
  merchantStatus: string | null;
  mapping: Record<string, string> | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  notes: string | null;
}

// Create payload: numeric fields accept numbers (the API coerces), everything
// else mirrors the row shape.
export type CommissionRuleInput = Partial<
  Omit<CommissionRule, "id" | "rateValue" | "flatAmount" | "amount">
> & {
  kind: CommissionRule["kind"];
  rateValue?: number | string | null;
  flatAmount?: number | string | null;
  amount?: number | string | null;
};

export interface CommissionAuditEntry {
  id: number;
  entityType: string;
  entityId: string;
  action: string;
  field: string | null;
  oldValue: unknown;
  newValue: unknown;
  reason: string | null;
  createdAt: string;
  actor: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
}

// One deal (processor + mid) across every report it appears in, plus its log.
export interface CommissionDealHistory {
  deal: {
    processor: string;
    mid: string;
    merchantName: string | null;
    approvalDate: string | null;
  };
  months: Array<{
    id: number;
    reportId: number | null;
    reportName: string | null;
    reportStatus: CommissionReport["status"] | null;
    datePaid: string | null;
    importId: number;
    importFilename: string;
    importProcessor: string;
    importRegion: string | null;
    processingMonth: string;
    agentEmail: string | null;
    homeType: string | null;
    terminationReason: string | null;
    volume: string | null;
    transactions: number | null;
    grossProfit: string;
    expenseTotal: string;
    netRevenue: string | null;
    commission: string;
    msdProfit: string | null;
    rateModel: string;
    rateValue: string | null;
    ruleScope: string | null;
    reviewStatus: "ok" | "needs_review";
  }>;
  log: CommissionAuditEntry[];
}

// One row per terminated deal for the global Terminated list.
export interface CommissionTerminatedDeal {
  id: number;
  processor: string;
  mid: string;
  merchantName: string | null;
  terminationReason: string | null;
  processingMonth: string;
  volume: string | null;
  grossProfit: string;
  msdProfit: string | null;
  reportId: number | null;
  reportName: string | null;
  importId: number;
  importFilename: string;
}

export const commissionsApi = {
  // Reports — the payout-cycle containers (name + processing month + date paid).
  createReport: (body: {
    name: string;
    processingMonth: string;
    datePaid?: string | null;
    notes?: string | null;
  }) => api.post<CommissionReport>("/api/commission-reports", body),
  listReports: (params?: { agentEmail?: string }) => {
    const q =
      params?.agentEmail && params.agentEmail.trim()
        ? `?agentEmail=${encodeURIComponent(params.agentEmail.trim())}`
        : "";
    return api.get<CommissionReport[]>(`/api/commission-reports${q}`);
  },
  getReport: (id: number) =>
    api.get<CommissionReport>(`/api/commission-reports/${id}`),
  updateReport: (
    id: number,
    body: {
      name?: string;
      status?: Exclude<CommissionReport["status"], "paid">;
      datePaid?: string | null;
      notes?: string | null;
    },
  ) => api.patch<CommissionReport>(`/api/commission-reports/${id}`, body),
  deleteReport: (id: number) =>
    api.delete<{ deleted: boolean }>(`/api/commission-reports/${id}`),

  // Per-agent payouts: stamp a single unpaid agent, reopen a paid agent, or
  // stamp every still-unpaid payable agent. The report rolls up to 'paid'
  // once every payable agent has a paid payout. Already-paid stamps are not
  // overwritten; reopen first.
  markAgentPaid: (
    reportId: number,
    body: {
      agentEmail: string;
      datePaid?: string | null;
      notes?: string | null;
    },
  ) =>
    api.post<CommissionAgentPayout>(
      `/api/commission-reports/${reportId}/agent-payouts`,
      body,
    ),
  // The agent's "statement ready" email: preview, send to a paid agent, or a test copy to the caller.
  previewStatementEmail: (reportId: number, agentEmail: string) =>
    api.get<StatementEmailPreview>(
      `/api/commission-reports/${reportId}/agent-payouts/email?${new URLSearchParams({ agentEmail })}`,
    ),
  sendStatementEmail: (reportId: number, agentEmail: string) =>
    api.post<StatementEmailResult>(
      `/api/commission-reports/${reportId}/agent-payouts/email`,
      { agentEmail },
    ),
  sendStatementTestEmail: (reportId: number, agentEmail: string) =>
    api.post<StatementEmailResult & { to: string }>(
      `/api/commission-reports/${reportId}/agent-payouts/email/test`,
      { agentEmail },
    ),
  // Who is copied on statement emails: the every-email list, plus one agent's when given.
  listEmailCopies: (agentEmail?: string) =>
    api.get<CommissionEmailCopy[]>(
      `/api/commission-reports/email-copies${agentEmail ? `?${new URLSearchParams({ agentEmail })}` : ""}`,
    ),
  addEmailCopy: (body: { agentEmail: string | null; email: string; kind: "cc" | "bcc" }) =>
    api.post<CommissionEmailCopy>("/api/commission-reports/email-copies", body),
  removeEmailCopy: (id: number) => api.delete<{ removed: number }>(`/api/commission-reports/email-copies/${id}`),
  reportActivity: (reportId: number, agentEmail?: string) =>
    api.get<ReportActivityEntry[]>(
      `/api/commission-reports/${reportId}/activity${agentEmail ? `?${new URLSearchParams({ agentEmail })}` : ""}`,
    ),
  // Manual send of one agent's statement to any address, in the agent's or the internal view.
  sendStatementTo: (reportId: number, body: { agentEmail: string; to: string; view: "agent" | "internal" }) =>
    api.post<StatementEmailResult & { invited: boolean; expiresAt: string | null }>(
      `/api/commission-reports/${reportId}/agent-payouts/email/send`,
      body,
    ),
  revokeStatementAccess: (reportId: number, previewId: number) =>
    api.post<{ revoked: number }>(`/api/commission-reports/${reportId}/agent-payouts/email/revoke`, { previewId }),
  // Bulk send: the paid agents not emailed yet, and sending to all of them.
  unsentStatementEmails: (reportId: number) =>
    api.get<{ unsent: string[]; alreadySent: number }>(
      `/api/commission-reports/${reportId}/agent-payouts/email/all`,
    ),
  sendUnsentStatementEmails: (reportId: number) =>
    api.post<StatementEmailBatch>(`/api/commission-reports/${reportId}/agent-payouts/email/all`, {}),
  reopenAgentPaid: (reportId: number, agentEmail: string) =>
    api.post<CommissionAgentPayout>(
      `/api/commission-reports/${reportId}/agent-payouts/reopen`,
      {
        agentEmail,
      },
    ),
  markAllAgentsPaid: (
    reportId: number,
    body: { datePaid?: string | null } = {},
  ) =>
    api.post<CommissionReport>(
      `/api/commission-reports/${reportId}/agent-payouts/all`,
      body,
    ),

  // Manually add deals (no file) — core raw inputs only; owner + rate/expense
  // formulas are set afterward in the resolve stage.
  manualImport: (body: {
    reportId: number;
    processor: string;
    deals: Array<{
      mid: string;
      merchantName: string;
      grossProfit: number;
      volume?: number;
      transactions?: number;
      chargebackCount?: number;
      region?: "US" | "CA" | null;
    }>;
  }) =>
    api.post<{ import: CommissionImport; commissions: CommissionRow[] }>(
      "/api/commission-imports/manual",
      body,
    ),

  // Suggest the processor a file came from (column layout + MIDs already on file). Stores nothing.
  detectProcessor: async (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${API_BASE}/api/commission-imports/detect`, {
      method: "POST",
      credentials: "include",
      body: fd,
    });
    if (!res.ok) {
      const payload = (await res.json().catch(() => null)) as {
        error?: { type?: string; message?: string; details?: unknown };
      } | null;
      throw new ApiError(
        res.status,
        payload?.error?.message ?? `Detection failed (${res.status})`,
        payload?.error?.type,
        payload?.error?.details,
      );
    }
    const json = (await res.json()) as { data: ProcessorDetection };
    return json.data;
  },

  // Upload a processor source file INTO a report → creates the import + returns a selectable preview.
  // A 409 with `details.code === 'DUPLICATE_IMPORT'` means the same data was
  // imported before; re-send with `onDuplicate` once the operator has chosen.
  upload: async (
    file: File,
    reportId: number,
    processor = "elavon",
    onDuplicate?: "replace" | "keep",
  ) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("processor", processor);
    fd.append("reportId", String(reportId));
    if (onDuplicate) fd.append("onDuplicate", onDuplicate);
    const res = await fetch(`${API_BASE}/api/commission-imports`, {
      method: "POST",
      credentials: "include",
      body: fd,
    });
    if (!res.ok) {
      const payload = (await res.json().catch(() => null)) as {
        error?: { type?: string; message?: string; details?: unknown };
      } | null;
      throw new ApiError(
        res.status,
        payload?.error?.message ?? `Upload failed (${res.status})`,
        payload?.error?.type,
        payload?.error?.details,
      );
    }
    const json = (await res.json()) as {
      data: { import: CommissionImport; preview: CommissionPreviewRow[] };
    };
    return json.data;
  },
  listImports: () => api.get<CommissionImport[]>("/api/commission-imports"),
  getImport: (id: number) =>
    api.get<CommissionImport>(`/api/commission-imports/${id}`),
  previewImport: (id: number, params?: { mid?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.mid) qs.set("mid", params.mid);
    if (params?.limit) qs.set("limit", String(params.limit));
    const q = qs.toString();
    return api.get<{
      headers: string[];
      mapping: Record<string, string>;
      skipped: number;
      total: number;
      rows: Array<CommissionPreviewRow & { raw: Record<string, string> }>;
    }>(`/api/commission-imports/${id}/preview${q ? `?${q}` : ""}`);
  },
  // Resolve owners for many deals in one call (one assignment rule each).
  resolveOwners: (importId: number, resolutions: CommissionOwnerResolution[]) =>
    api.post<{ resolved: number }>(
      `/api/commission-imports/${importId}/assignments`,
      { resolutions },
    ),
  // Re-run the rules on every deal already in a draft report.
  recalculateReport: (reportId: number) =>
    api.post<{ imports: number; deals: number }>(
      `/api/commission-reports/${reportId}/recalculate`,
      {},
    ),
  // Calculate the selected MIDs, or every parsed row with `all: true`.
  calculate: (
    id: number,
    body: { mids?: string[]; all?: boolean; mapping?: Record<string, string> },
  ) =>
    api.post<{ import: CommissionImport; commissions: CommissionRow[] }>(
      `/api/commission-imports/${id}/calculate`,
      body,
    ),
  deleteImport: (id: number) =>
    api.delete<{ deleted: boolean }>(`/api/commission-imports/${id}`),
  // Per-deal editor save: new rate + full expense line-item set. The backend
  // recomputes commission/net/expenses/MSD with the same engine as the import.
  updateCommission: (
    id: number,
    body: {
      rateModel: string;
      rateValue: number | null;
      flatAmount?: number | null;
      expenses: Array<{
        expenseKey: string;
        label: string;
        amountType: string;
        amount: number;
        expenseClass: string;
        appliesRegion?: string | null;
        basisMid?: string | null;
      }>;
      // Changes the deal's owner (writes the assignment rule) alongside the edit.
      owner?: {
        kind: "agent" | "msd" | "terminated" | "unassigned";
        agentEmail: string | null;
      };
      // Re-files the deal (and its owner) under this processor.
      processor?: string;
      // This month's inputs, when they differ from the statement.
      inputs?: {
        grossProfit: number;
        volume: number;
        transactions: number;
        chargebackCount: number;
      };
      // Override mode: hand-set figures and the terminated pay flag.
      overrides?: {
        commission?: number | null;
        msdProfit?: number | null;
        payTerminated?: boolean;
      };
      reason: string;
      scope?: "month" | "recurring";
    },
  ) => api.patch<CommissionRow>(`/api/commissions/${id}`, body),
  // Back to the rules: drops a hand-edited deal's figures and recalculates it. Returns the new row.
  // Hide a deal from the agent's statement, show it, or (null) leave it to the rule. Not money.
  setAgentVisibility: (id: number, agentVisibility: "hidden" | "shown" | null) =>
    api.patch<CommissionRow>(`/api/commissions/${id}/visibility`, { agentVisibility }),
  renameImport: (id: number, filename: string) =>
    api.patch<CommissionImport>(`/api/commission-imports/${id}`, { filename }),
  // Move every deal one agent holds in the report to another agent.
  // Move an agent's deals to another agent: the listed ones, or all of them when commissionIds is null.
  reassignAgent: (reportId: number, from: string, to: string, commissionIds: number[] | null = null) =>
    api.post<{ moved: number; whole: boolean; agentRules: number }>(`/api/commission-reports/${reportId}/reassign-agent`, {
      from,
      to,
      ...(commissionIds ? { commissionIds } : {}),
    }),
  // Undo every hand edit in a report, or one agent's: pinned deals go back to their statement and the rules.
  resetHandEdits: (reportId: number, agentEmail: string | null) =>
    api.post<{ reset: number; skipped: number }>(`/api/commission-reports/${reportId}/reset-hand-edits`, { agentEmail }),
  resetDeal: (id: number) => api.post<CommissionRow | null>(`/api/commissions/${id}/reset`, {}),
  // The deal's staff-only note, saved on its own (no reason, no recompute).
  updateInternalNote: (id: number, internalNote: string | null) =>
    api.patch<CommissionRow>(`/api/commissions/${id}`, { internalNote }),
  // The stored processor file behind an import (INTERNAL only; audited).
  sourceDownloadUrl: (importId: number) =>
    `${API_BASE}/api/commission-imports/${importId}/download`,
  /** Fetch the stored source file as an ArrayBuffer for in-app rendering. */
  fetchSourceFile: async (importId: number) => {
    const res = await fetch(
      `${API_BASE}/api/commission-imports/${importId}/download`,
      { credentials: "include" },
    );
    if (!res.ok) {
      const payload = (await res.json().catch(() => null)) as {
        error?: { message?: string };
      } | null;
      throw new ApiError(
        res.status,
        payload?.error?.message ?? `Failed to download source (${res.status})`,
      );
    }
    const disposition = res.headers.get("Content-Disposition") ?? "";
    const match = /filename="([^"]+)"/i.exec(disposition);
    const buffer = await res.arrayBuffer();
    return { buffer, filename: match?.[1] ?? `import-${importId}` };
  },
  // The month's statement folder on disk (COMMISSION_SOURCE_DIR/<Month> Commissions).
  listSourceFiles: (month: string) =>
    api.get<{ folder: string; files: CommissionSourceFile[] }>(
      `/api/commission-sources?month=${encodeURIComponent(month.slice(0, 10))}`,
    ),
  sourceFileUrl: (month: string, path: string) =>
    `${API_BASE}/api/commission-sources/file?month=${encodeURIComponent(month.slice(0, 10))}&path=${encodeURIComponent(path)}`,
  dealHistory: (id: number) =>
    api.get<CommissionDealHistory>(`/api/commissions/${id}/history`),
  listTerminatedDeals: () =>
    api.get<CommissionTerminatedDeal[]>("/api/commissions/terminated"),
  listRules: (params?: {
    processor?: string;
    mid?: string;
    kind?: string;
    agentEmail?: string;
    scope?: "deal" | "default";
  }) => {
    const qs = new URLSearchParams();
    if (params?.processor) qs.set("processor", params.processor);
    if (params?.mid) qs.set("mid", params.mid);
    if (params?.kind) qs.set("kind", params.kind);
    if (params?.agentEmail) qs.set("agentEmail", params.agentEmail);
    if (params?.scope) qs.set("scope", params.scope);
    const q = qs.toString();
    return api.get<CommissionRule[]>(
      `/api/commission-rules${q ? `?${q}` : ""}`,
    );
  },
  createRule: (body: CommissionRuleInput) =>
    api.post<CommissionRule>("/api/commission-rules", body),
  updateRule: (id: number, body: Partial<CommissionRule>) =>
    api.patch<CommissionRule>(`/api/commission-rules/${id}`, body),
  deleteRule: (id: number) =>
    api.delete<{ deleted: boolean }>(`/api/commission-rules/${id}`),
};

// ─── Agent commission statements ─────────────────────────────────────────────
// Identity-scoped, read-only: the caller's own approved months. Two states only
// — Pending (approved, not yet paid) and Paid. Drafts never appear. Nothing
// MSD-internal (profit, rules, expenses, source rows) is in this shape.
export interface AgentStatementSummary {
  reportId: number;
  name: string;
  processingMonth: string;
  // preview: staff sent this statement to check it; it is not paid and may not be final.
  status: "pending" | "paid" | "preview";
  datePaid: string | null;
  dealCount: number;
  volume: number;
  transactions: number;
  commission: number;
}

export interface AgentStatementRow {
  id: number;
  mid: string;
  merchantName: string | null;
  processor: string;
  // 'terminated': not paid on this deal; its figures are withheld (null) and only the reason shows.
  status: "active" | "terminated" | "terminated_paid";
  closeReason: string | null;
  volume: number | null;
  transactions: number | null;
  commission: number | null;
  processingMonth: string;
}

export interface AgentStatement extends AgentStatementSummary {
  rows: AgentStatementRow[];
}

export const agentStatementsApi = {
  list: () => api.get<AgentStatementSummary[]>("/api/commission-statements"),
  get: (reportId: number) =>
    api.get<AgentStatement>(`/api/commission-statements/${reportId}`),
};

// ─── Product inquiries ───────────────────────────────────────────────────────
// Replaces the legacy Supabase `product-inquiry` edge function. The requester's
// identity is taken from the session server-side, so the client only sends the
// product + optional note.
export const productInquiriesApi = {
  submit: (body: { productRequested: string; additionalInfo?: string }) =>
    api.post<{ sent: boolean }>("/api/product-inquiries", body),
};

// ─── Mailroom ────────────────────────────────────────────────────────────────
// Internal email tooling: block-based template CRUD, one-off sends via Resend,
// and a delivery/engagement log fed by the Resend webhook. The rich block /
// settings / event shapes live in components/mailroom/types.ts (the single
// source of truth shared with the builder + renderer).
import type {
  EmailBlock,
  EmailLog,
  EmailTemplate,
  TemplateSettings,
} from "@/components/mailroom/types";

export interface MailroomTemplateInput {
  name: string;
  description?: string;
  category?: string;
  subject: string;
  fromAddress?: string;
  replyTo?: string;
  previewText?: string;
  blocks: EmailBlock[];
  settings: TemplateSettings;
  sampleData: Record<string, string>;
}

export interface MailroomSendInput {
  /** Optional link back to the template this send was composed from. */
  templateId?: string;
  /** Optional — API falls back to RESEND_FROM_EMAIL when omitted. */
  from?: string;
  /** Single recipient — Mailroom sends one email at a time. */
  to: string;
  subject: string;
  /** Fully-rendered email HTML produced by the block builder. */
  html: string;
  replyTo?: string;
}

// Resend-backed audience types (contacts, groups, custom-field schema).
export type ContactPropertyType = "string" | "number";

export interface ContactProperty {
  id: string;
  key: string;
  type: ContactPropertyType;
  fallbackValue: string | number | null;
}

export interface Segment {
  id: string;
  name: string;
  createdAt: string | null;
}

export interface Contact {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  unsubscribed: boolean;
  properties: Record<string, string | number | null>;
  segmentIds: string[];
  createdAt: string | null;
}

export interface ContactInput {
  email: string;
  firstName?: string;
  lastName?: string;
  unsubscribed?: boolean;
  properties?: Record<string, string | number>;
  segmentIds?: string[];
}

export const mailroomApi = {
  listTemplates: () => api.get<EmailTemplate[]>("/api/mailroom/templates"),
  getTemplate: (id: string) =>
    api.get<EmailTemplate>(`/api/mailroom/templates/${id}`),
  createTemplate: (body: MailroomTemplateInput) =>
    api.post<EmailTemplate>("/api/mailroom/templates", body),
  updateTemplate: (id: string, body: MailroomTemplateInput) =>
    api.put<EmailTemplate>(`/api/mailroom/templates/${id}`, body),
  deleteTemplate: (id: string) =>
    api.delete<{ id: string; deleted: boolean }>(
      `/api/mailroom/templates/${id}`,
    ),
  send: (body: MailroomSendInput) =>
    api.post<EmailLog>("/api/mailroom/send", body),
  /** Batch send pre-rendered emails (one Resend request per 100). INTERNAL. */
  sendBatch: (items: MailroomSendInput[]) =>
    api.post<EmailLog[]>("/api/mailroom/send-batch", { items }),
  listLogs: () => api.get<EmailLog[]>("/api/mailroom/logs"),
  getLog: (id: string) => api.get<EmailLog>(`/api/mailroom/logs/${id}`),

  // Contact properties (custom-field schema — the per-participant variable catalog)
  listContactProperties: () =>
    api.get<ContactProperty[]>("/api/mailroom/contact-properties"),
  createContactProperty: (body: {
    key: string;
    type: ContactPropertyType;
    fallbackValue?: string | number;
  }) => api.post<{ id: string }>("/api/mailroom/contact-properties", body),
  deleteContactProperty: (id: string) =>
    api.delete<{ id: string }>(`/api/mailroom/contact-properties/${id}`),

  // Groups (Resend segments)
  listGroups: () => api.get<Segment[]>("/api/mailroom/groups"),
  createGroup: (name: string) =>
    api.post<Segment>("/api/mailroom/groups", { name }),
  deleteGroup: (id: string) =>
    api.delete<{ id: string }>(`/api/mailroom/groups/${id}`),

  // Group send — create + send a Resend broadcast to a group (segment).
  sendBroadcast: (body: {
    segmentId: string;
    from?: string;
    subject: string;
    html: string;
    previewText?: string;
    replyTo?: string;
    name?: string;
    scheduledAt?: string;
    templateId?: string;
  }) => api.post<{ id: string }>("/api/mailroom/broadcasts", body),

  // Contacts (Resend-backed)
  listContacts: (segmentId?: string) =>
    api.get<Contact[]>(
      `/api/mailroom/contacts${segmentId ? `?segmentId=${encodeURIComponent(segmentId)}` : ""}`,
    ),
  getContact: (id: string) => api.get<Contact>(`/api/mailroom/contacts/${id}`),
  createContact: (body: ContactInput) =>
    api.post<{ id: string }>("/api/mailroom/contacts", body),
  updateContact: (id: string, body: Partial<ContactInput>) =>
    api.put<{ id: string }>(`/api/mailroom/contacts/${id}`, body),
  deleteContact: (id: string) =>
    api.delete<{ id: string }>(`/api/mailroom/contacts/${id}`),
};

// ─── Error reporting ──────────────────────────────────────────────────────────

export interface ErrorReportInput {
  note?: string;
  errorMessage?: string;
  stack?: string;
  componentStack?: string;
  pageUrl?: string;
  userAgent?: string;
}

export const errorReportApi = {
  submit: (body: ErrorReportInput) =>
    api.post<{ sent: boolean }>("/api/error-report", body),
};

// ─── Announcements ────────────────────────────────────────────────────────────
// "What's new" entries shown in a dialog on first login (role-targeted, seen
// once per user server-side) and managed via the admin CRUD page.

export interface Announcement {
  id: number;
  title: string;
  body: string;
  imageUrl: string | null;
  /** Roles this shows to. Empty = everyone. */
  targetRoles: string[];
  isActive: boolean;
  publishedAt: string;
  expiresAt: string | null;
  createdByUserId: string | null;
  createdBy: UserRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface AnnouncementListResponse {
  data: Announcement[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export interface ListAnnouncementsParams {
  page?: number;
  limit?: number;
  search?: string;
  sort?: string;
  dir?: "asc" | "desc";
}

export interface AnnouncementInput {
  title: string;
  body: string;
  imageUrl?: string | null;
  targetRoles?: string[];
  isActive?: boolean;
  publishedAt?: string;
  expiresAt?: string | null;
}

export const announcementsApi = {
  // User-facing feed for the first-login dialog. `no-store` so a cached response
  // is never reused across different signed-in users in the same browser.
  listUnseen: () =>
    api.get<Announcement[]>("/api/announcements/unseen", { cache: "no-store" }),
  markSeen: (id: number) => api.post<void>(`/api/announcements/${id}/seen`),

  // Admin CRUD. `list` bypasses the data-unwrapping helper to keep `meta.total`
  // for the toolbar count + infinite scroll.
  list: async (
    params: ListAnnouncementsParams = {},
  ): Promise<AnnouncementListResponse> => {
    const sp = new URLSearchParams();
    if (params.page) sp.set("page", String(params.page));
    if (params.limit) sp.set("limit", String(params.limit));
    if (params.search) sp.set("search", params.search);
    if (params.sort) sp.set("sort", params.sort);
    if (params.dir) sp.set("dir", params.dir);
    const qs = sp.toString() ? `?${sp.toString()}` : "";

    const res = await fetch(`${API_URL}/api/announcements${qs}`, {
      credentials: "include",
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) {
      const err = (
        payload as {
          error?: { message?: string; type?: string; details?: unknown };
        } | null
      )?.error;
      throw new ApiError(
        res.status,
        err?.message ?? `Request failed (${res.status})`,
        err?.type,
        err?.details,
      );
    }
    return payload as AnnouncementListResponse;
  },
  create: (body: AnnouncementInput) =>
    api.post<Announcement>("/api/announcements", body),
  update: (id: number, body: Partial<AnnouncementInput>) =>
    api.patch<Announcement>(`/api/announcements/${id}`, body),
  remove: (id: number) => api.delete<void>(`/api/announcements/${id}`),
};

// ─── Google (Gmail) integration — INTERNAL only ─────────────────────────────

export type GoogleIntegrationStatus = "ACTIVE" | "NEEDS_RECONNECT";

export interface GoogleConnection {
  configured: boolean;
  connected: boolean;
  status: GoogleIntegrationStatus | null;
  accountEmail: string | null;
  connectedAt: string | null;
}

// `details.code` the API attaches to Gmail errors so the UI can pick the right state.
export type GmailErrorCode =
  | "GMAIL_NOT_CONFIGURED"
  | "GMAIL_NOT_CONNECTED"
  | "GMAIL_RECONNECT_REQUIRED";

export function gmailErrorCode(err: unknown): GmailErrorCode | undefined {
  if (!(err instanceof ApiError)) return undefined;
  const code = (err.details as { code?: unknown } | null | undefined)?.code;
  return typeof code === "string" && code.startsWith("GMAIL_")
    ? (code as GmailErrorCode)
    : undefined;
}

export const googleIntegrationApi = {
  status: () => api.get<GoogleConnection>("/api/integrations/google/status"),
  disconnect: () => api.delete<void>("/api/integrations/google"),
  // Full-page navigation target — the API redirects on to Google's consent screen.
  connectUrl: (returnTo: string) =>
    `${API_URL}/api/integrations/google/connect?returnTo=${encodeURIComponent(returnTo)}`,
};

export interface EmailAddress {
  name: string | null;
  email: string;
}

export interface EmailThreadSummary {
  id: string;
  subject: string;
  snippet: string;
  messageCount: number;
  lastMessageAt: string | null;
  lastMessageFrom: EmailAddress | null;
  participants: EmailAddress[];
  unread: boolean;
  hasUnreadMerchantReply: boolean;
  status: "merchant_replied" | "awaiting_merchant";
  gmailUrl: string;
}

export interface EmailActivityPage {
  trackedEmails: string[];
  trackedDomains: string[];
  threads: EmailThreadSummary[];
  nextPageToken: string | null;
}

export interface EmailAttachment {
  partId: string;
  filename: string;
  mimeType: string;
  size: number;
}

export interface EmailMessage {
  id: string;
  from: EmailAddress | null;
  to: EmailAddress[];
  cc: EmailAddress[];
  date: string | null;
  snippet: string;
  unread: boolean;
  /** Sanitized by the API; render only inside a sandboxed iframe. */
  html: string;
  attachments: EmailAttachment[];
  fromMerchant: boolean;
}

export interface EmailThreadDetail {
  id: string;
  subject: string;
  gmailUrl: string;
  messages: EmailMessage[];
}

export type EmailTrackerKind = "EMAIL" | "DOMAIN";

export interface EmailTracker {
  id: string;
  kind: EmailTrackerKind;
  value: string;
  createdAt: string;
}

// Trackers are private to the signed-in user; other internal users never see them.
export interface EmailTrackerList {
  // Emails taken from the application itself (contact, owners, link recipients) — read-only; null without an application.
  applicationEmails: string[] | null;
  trackers: EmailTracker[];
}

// Base path of an Email Activity endpoint set; branded so a bare id can't be passed by mistake.
export type EmailActivityScope = string & {
  readonly __brand: "EmailActivityScope";
};

export const emailActivityScope = {
  application: (applicationId: string) =>
    `/api/applications/${encodeURIComponent(applicationId)}/email-activity` as EmailActivityScope,
  // The API falls back to the merchant's application when one is linked.
  merchant: (merchantId: number) =>
    `/api/merchants/${merchantId}/email-activity` as EmailActivityScope,
};

export const emailActivityApi = {
  list: (scope: EmailActivityScope, pageToken?: string) =>
    api.get<EmailActivityPage>(
      `${scope}${pageToken ? `?pageToken=${encodeURIComponent(pageToken)}` : ""}`,
    ),
  thread: (scope: EmailActivityScope, threadId: string) =>
    api.get<EmailThreadDetail>(
      `${scope}/threads/${encodeURIComponent(threadId)}`,
    ),
  downloadAttachment: async (
    scope: EmailActivityScope,
    threadId: string,
    messageId: string,
    partId: string,
  ): Promise<Blob> => {
    const res = await fetch(
      `${API_BASE}${scope}/threads/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(partId)}`,
      { credentials: "include" },
    );
    if (!res.ok) {
      const payload = (await res.json().catch(() => null)) as {
        error?: { message?: string; type?: string };
      } | null;
      throw new ApiError(
        res.status,
        payload?.error?.message ?? `Download failed (${res.status})`,
        payload?.error?.type,
      );
    }
    return res.blob();
  },
  trackers: (scope: EmailActivityScope) =>
    api.get<EmailTrackerList>(`${scope}/trackers`),
  addTracker: (scope: EmailActivityScope, value: string) =>
    api.post<EmailTracker>(`${scope}/trackers`, { value }),
  removeTracker: (scope: EmailActivityScope, trackerId: string) =>
    api.delete<void>(`${scope}/trackers/${encodeURIComponent(trackerId)}`),
};
